import { CalendarDay } from './components/CalendarDay';
import { Month } from './components/Month';
import { WeekDays } from './components/WeekDays';
import { WORK_TYPE } from './constants';
import { DEFAULT_OPTIONS } from './defaultOptions';
import type { IOption, IResolvedOption } from './interfaces/IOption';
import { I18n } from './i18n/i18n';
import type { ICalendarDay, ICalendarDayMake } from './interfaces/ICalendarDay';
import { generateHtmlTemplate } from './template';
import type { PickerMode } from './types/PickerMode';
import { EventEmitter } from './events/EventEmitter';
import type {
  DateTimePickerEventType,
  DateTimePickerEventHandler,
  IDateTimePickerEventMap,
  InternalDateTimePickerEventMap,
} from './types/DateTimePickerEvents';
import {
  createDateWithTimezone,
  createDateTimeWithTimezone,
  getDateComponentsWithTimezone,
  formatDateByPattern,
  formatDigitsByPattern,
  isSameDayWithTimezone,
  parseDateByPattern,
  getWeekdayWithTimezone,
  resolveManagedInputType,
} from './utils/dateUtils';
import { instanceManager } from './components/InstanceManager.ts';

export class DateTimePicker {
  protected today: Date;
  protected options: IResolvedOption;

  protected weekDays: WeekDays;
  protected month: Month;

  protected instanseElement: HTMLElement;

  protected showingDate!: Date; // Отсюда будут интересовать только месяц и год
  protected previousMonth: number = -1;
  protected previousYear: number = -1;
  protected selectedDate!: Date[];
  protected appliedDate!: Date[];
  protected rangePreviewDate: Date | null = null;
  protected selectedTime: string;
  protected appliedTime: string;
  protected inputElement: HTMLInputElement | null = null;
  protected displayInputElement: HTMLInputElement | null = null;
  protected rangeEndInputElement: HTMLInputElement | null = null;
  protected rangeEndDisplayInputElement: HTMLInputElement | null = null;
  protected createdHostElement: boolean = false;
  protected mode: PickerMode;
  protected isPickerOpen: boolean = false;
  protected pickerView: 'calendar' | 'month' | 'year' = 'calendar';
  protected datetimeStep: 'date' | 'time' = 'date';
  protected timeChangedByUserDuringDateStep: boolean = false;
  protected yearPickerStart: number = 0;
  protected manualSubmitHandlers: Array<{
    form: HTMLFormElement;
    handler: EventListener;
  }> = [];

  protected i18n: I18n;
  protected eventEmitter: EventEmitter;
  protected static readonly MANAGED_INPUT_CLASS = 'dtp-managed-input';
  protected static readonly DISPLAY_INPUT_CLASS = 'dtp-display-input';
  protected static readonly VALUE_INPUT_CLASS = 'dtp-value-input';
  protected static readonly DATETIME_MODE_ALIASES = new Set<PickerMode>([
    'datetime',
    'datetime-local',
  ]);

  // Хранилище экземпляров
  protected static instances = new Map<string, DateTimePicker>();

  // Keyboard navigation state
  protected focusedDayIndex: number = -1; // -1 means no day focused
  protected focusedMonthIndex: number = -1; // -1 means no month focused (for month picker)
  protected focusedYearIndex: number = -1; // -1 means no year focused (for year picker)

  // Accessibility
  protected dialogId: string;

  // Last rendered calendar days for keyboard navigation
  protected lastRenderedDays: CalendarDay[] = [];

  // Window resize handler for adaptive positioning
  protected resizeHandler: ((this: Window, ev: UIEvent) => void) | null = null;
  protected keyboardNavigationHandler: ((event: KeyboardEvent) => void) | null = null;
  protected manuallyEditedInputs: WeakSet<HTMLInputElement> = new WeakSet();

  // Scroll position for mobile body scroll lock
  protected scrollPosition: number = 0;

  private cleanupHandlers: Array<() => void> = [];
  private destroyed = false;
  private disabledDayKeys = new Set<string>();
  private inputSnapshots = new Map<HTMLInputElement, Map<string, string | null>>();
  private inputIcons = new Map<HTMLInputElement, HTMLButtonElement>();
  private static scrollOwners = new Set<DateTimePicker>();
  private static bodySnapshot: string | null = null;
  private static bodyScrollY = 0;

  private lockBody(): void {
    if (DateTimePicker.scrollOwners.has(this)) return;
    if (DateTimePicker.scrollOwners.size === 0) {
      DateTimePicker.bodySnapshot = document.body.getAttribute('style');
      DateTimePicker.bodyScrollY = window.scrollY;
      Object.assign(document.body.style, {
        overflow: 'hidden',
        position: 'fixed',
        top: `-${window.scrollY}px`,
        width: '100%',
        left: '0',
      });
    }
    DateTimePicker.scrollOwners.add(this);
  }

  private unlockBody(): void {
    if (!DateTimePicker.scrollOwners.delete(this) || DateTimePicker.scrollOwners.size > 0) return;
    if (DateTimePicker.bodySnapshot === null) document.body.removeAttribute('style');
    else document.body.setAttribute('style', DateTimePicker.bodySnapshot);
    window.scrollTo(0, DateTimePicker.bodyScrollY);
  }

  constructor(selector: HTMLElement | string, options?: Partial<IOption>) {
    let el: HTMLElement | null = null;
    this.options = Object.assign(
      {},
      DEFAULT_OPTIONS,
      Object.fromEntries(Object.entries(options ?? {}).filter(([, value]) => value !== undefined)),
    );
    this.options.mode = this.normalizeModeOption(this.options.mode);
    this.options.displayFormatsByLang = {
      ...DEFAULT_OPTIONS.displayFormatsByLang,
      ...(options?.displayFormatsByLang ?? {}),
    };

    // Парсинг minDate и maxDate из строковых значений
    if (this.options.minDate !== null && this.options.minDate !== undefined) {
      this.options.minDate = this.parseDateOption(this.options.minDate);
    }
    if (this.options.maxDate !== null && this.options.maxDate !== undefined) {
      this.options.maxDate = this.parseDateOption(this.options.maxDate);
    }
    if (this.options.openToDate !== null && this.options.openToDate !== undefined) {
      this.options.openToDate = this.parseDateOption(this.options.openToDate);
    }

    if (
      this.options.minDate &&
      this.options.maxDate &&
      this.options.minDate > this.options.maxDate
    ) {
      throw new RangeError('minDate must not exceed maxDate');
    }
    this.disabledDayKeys = this.makeDisabledDayKeys(this.options.disabledDates);

    // Инициализация EventEmitter
    this.eventEmitter = new EventEmitter();

    // Генерация уникального ID для диалога (для accessibility)
    this.dialogId = 'dtp-dialog-' + Math.random().toString(36).substr(2, 9);

    this.i18n = new I18n({
      lang: this.options.lang,
      fallbackLang: this.options.fallbackLang,
      translations: this.options.translations,
    });

    if (typeof selector === 'string') {
      el = instanceManager.resolveElement(selector);
      if (el === null) {
        throw new Error('Элемент не найден на странице.');
      }
    } else {
      el = selector;
    }

    if (instanceManager.getInstance(el)) {
      throw new Error(
        'DateTimePicker уже инициализирован для этого элемента. Используйте DateTimePicker.reinit(...) для повторной инициализации.',
      );
    }

    this.month = new Month();
    this.instanseElement = this.prepareInstanceElement(el);
    this.rangeEndInputElement = this.resolveRangeEndInput();
    this.mode = this.resolveMode();
    if (this.mode === 'month') {
      this.pickerView = 'month';
    }
    this.syncDisplayInputPlaceholders();
    this.assertModeMatchesInputType();
    this.weekDays = new WeekDays(this.options.startWeek);
    // Создаем текущую дату с учетом часового пояса
    const now = new Date();
    const nowComponents = getDateComponentsWithTimezone(now, this.options.timezone);
    this.today = createDateWithTimezone(
      nowComponents.year,
      nowComponents.month,
      nowComponents.day,
      this.options.timezone,
    );
    this.selectedDate = [];
    this.appliedDate = [];
    this.selectedTime = this.getTimeValue(this.today);
    this.appliedTime = this.selectedTime;
    this.initSelectedDateFromInput();
    if (!this.isSelectionValid(this.selectedDate)) {
      this.selectedDate = [];
      this.appliedDate = [];
    }
    this.updateRangeEndInputVisibility();
    this.syncSelectedDateToInputs();
    this.setShowingDate(this.resolveInitialShowingDate());
    const showingComponents = getDateComponentsWithTimezone(
      this.showingDate,
      this.options.timezone,
    );
    this.previousMonth = showingComponents.month;
    this.previousYear = showingComponents.year;
    this.yearPickerStart = showingComponents.year - 6;

    this.injectTemplate();
    this.applyModeVisibility();
    this.renderMonthPicker();
    this.renderYearPicker();
    this.syncTimeUi();
    this.setDayHeader();
    this.setCalendarHeader();

    const res = this.getAvailableMonth();
    this.setCalendarDays(res);
    this.updateSelectedDateLabel();
    this.setPickerOpen(false);

    this.addEventListeners();
    this.registerOptionCallbacks();

    this.registerInstance();
  }

  /**
   * Регистрирует экземпляр в менеджере
   */
  private registerInstance(): void {
    // Пытаемся зарегистрировать по ID display input (он получает оригинальный ID)
    const elements = [this.instanseElement, this.inputElement, this.displayInputElement].filter(
      (element): element is HTMLElement => element instanceof HTMLElement,
    );

    if (this.displayInputElement?.id) {
      instanceManager.register(this.displayInputElement.id, this, elements);
      return;
    }

    // Если display input без ID, используем data-dtp-original-id или ID value input
    if (this.inputElement) {
      const originalId = this.inputElement.dataset.dtpOriginalId || this.inputElement.id;
      if (originalId) {
        instanceManager.register(originalId, this, elements);
        return;
      }
    }

    instanceManager.register(null, this, elements);
  }

  /**
   * Удаляет экземпляр из менеджера
   */
  private unregisterInstance(): void {
    const elements = [this.instanseElement, this.inputElement, this.displayInputElement].filter(
      (element): element is HTMLElement => element instanceof HTMLElement,
    );

    if (this.displayInputElement?.id) {
      instanceManager.unregister(this.displayInputElement.id, elements);
      return;
    }

    if (this.inputElement) {
      const originalId = this.inputElement.dataset.dtpOriginalId || this.inputElement.id;
      if (originalId) {
        instanceManager.unregister(originalId, elements);
        return;
      }
    }

    instanceManager.unregister(null, elements);
  }

  /**
   * Парсит опцию даты, которая может быть строкой или объектом Date
   * @param dateOption Значение даты (Date, string или null)
   * @returns Объект Date или null
   */
  protected parseDateOption(dateOption: Date | string | null): Date | null {
    if (dateOption === null || dateOption === undefined) return null;
    let result: Date | null;
    if (dateOption instanceof Date) result = this.cloneDate(dateOption);
    else if (/^\d{4}-\d{2}-\d{2}$/.test(dateOption)) {
      result = parseDateByPattern(dateOption, 'yyyy-MM-dd', this.options.timezone);
    } else result = new Date(dateOption);
    if (!result || !Number.isFinite(result.getTime())) throw new RangeError('Invalid date option');
    return result;
  }

  /**
   * Проверяет, находится ли дата в допустимом диапазоне (minDate <= date <= maxDate)
   * @param date Дата для проверки
   * @returns true если дата валидна
   */
  private dayKey(date: Date): string {
    return formatDateByPattern(date, 'yyyy-MM-dd', this.options.timezone);
  }

  private makeDisabledDayKeys(dates: Array<Date | string>): Set<string> {
    return new Set(
      dates.map(value => {
        if (typeof value === 'string' && !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          throw new RangeError('disabledDates strings must use YYYY-MM-DD');
        }
        const date = this.parseDateOption(value);
        if (!date) throw new RangeError('Invalid disabled date');
        return this.dayKey(date);
      }),
    );
  }

  private isCalendarDayValid(date: Date): boolean {
    const { minDate, maxDate } = this.options;
    if (!Number.isFinite(date.getTime())) return false;
    if (minDate !== null && date < minDate) return false;
    if (maxDate !== null && date > maxDate) return false;
    if (this.mode === 'time') return true;
    return (
      !this.disabledDayKeys.has(this.dayKey(date)) &&
      !this.options.isDateDisabled?.(this.cloneDate(date), this)
    );
  }

  protected isDateValid(date: Date): boolean {
    if (!Number.isFinite(date.getTime())) return false;
    if (this.mode !== 'month') return this.isCalendarDayValid(date);
    const { year, month } = getDateComponentsWithTimezone(date, this.options.timezone);
    const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    for (let day = 1; day <= days; day++) {
      if (this.isCalendarDayValid(createDateWithTimezone(year, month, day, this.options.timezone)))
        return true;
    }
    return false;
  }

  private isSelectionValid(dates: Date[]): boolean {
    if (dates.some(date => !this.isDateValid(date))) return false;
    if (dates.length !== 2) return true;
    if (dates[0] > dates[1]) return false;
    if (this.mode === 'time') return true;
    if (this.disabledDayKeys.size === 0 && !this.options.isDateDisabled) return true;
    // Walk calendar days, not 24-hour durations (DST days may have 23/25 hours).
    let cursor = this.cloneDate(dates[0]);
    while (this.dayKey(cursor) < this.dayKey(dates[1])) {
      const { year, month, day } = getDateComponentsWithTimezone(cursor, this.options.timezone);
      cursor = createDateWithTimezone(year, month, day + 1, this.options.timezone);
      if (!this.isDateValid(cursor)) return false;
    }
    return true;
  }

  /** Re-evaluate availability after external data changes. Invalid selections are cleared. */
  refresh(): this {
    this.disabledDayKeys = this.makeDisabledDayKeys(this.options.disabledDates);
    if (!this.isSelectionValid(this.selectedDate)) this.clear();
    else this.syncSelectedDateToInputs();
    this.changeDate();
    this.syncTimeUi();
    return this;
  }

  protected cloneDate(date: Date): Date {
    return new Date(date.getTime());
  }

  protected getClampedDate(date: Date): Date {
    const { minDate, maxDate } = this.options;

    if (minDate !== null && date < minDate) {
      return this.cloneDate(minDate);
    }
    if (maxDate !== null && date > maxDate) {
      return this.cloneDate(maxDate);
    }

    return this.cloneDate(date);
  }

  protected resolveDefaultDate(): Date {
    if (this.options.openToDate instanceof Date) {
      return this.getClampedDate(this.options.openToDate);
    }

    return this.getClampedDate(this.today);
  }

  protected resolveInitialShowingDate(): Date {
    if (this.selectedDate[0] instanceof Date) {
      return this.cloneDate(this.selectedDate[0]);
    }

    return this.resolveDefaultDate();
  }

  /**
   * Регистрирует callback-функции из options как обработчики событий
   */
  protected registerOptionCallbacks(): void {
    // Регистрация onOpen
    {
      this.on('open', event => {
        this.options.onOpen?.(event.detail.picker);
      });
    }

    // Регистрация onClose
    {
      this.on('close', event => {
        this.options.onClose?.(event.detail.picker);
      });
    }

    // Регистрация onSelect
    {
      this.on('select', event => {
        this.options.onSelect?.(event.detail.date, event.detail.picker);
      });
    }

    // Регистрация onMonthChange
    {
      this.on('monthChange', event => {
        this.options.onMonthChange?.(event.detail.month, event.detail.year, event.detail.picker);
      });
    }

    // Регистрация onYearChange
    {
      this.on('yearChange', event => {
        this.options.onYearChange?.(event.detail.year, event.detail.picker);
      });
    }

    // Регистрация onTimeChange
    {
      this.on('timeChange', event => {
        this.options.onTimeChange?.(event.detail.time, event.detail.picker);
      });
    }

    // Регистрация onViewChange
    {
      this.on('viewChange', event => {
        this.options.onViewChange?.(event.detail.view, event.detail.picker);
      });
    }

    // Регистрация onDestroy
    {
      this.on('destroy', event => {
        this.options.onDestroy?.(event.detail.picker);
      });
    }
  }

  protected getPickerWrapper(): HTMLElement | null {
    return this.instanseElement.querySelector('.dtp-wrapper');
  }

  protected setPickerOpen(isOpen: boolean): void {
    const wrapper = this.getPickerWrapper();
    if (!(wrapper instanceof HTMLElement)) {
      return;
    }

    wrapper.hidden = !isOpen;
    this.isPickerOpen = isOpen;

    // Обновить aria-expanded у видимых input элементов
    if (this.displayInputElement) {
      this.displayInputElement.setAttribute('aria-expanded', isOpen.toString());
    }
    if (this.rangeEndDisplayInputElement) {
      this.rangeEndDisplayInputElement.setAttribute('aria-expanded', isOpen.toString());
    }
    this.syncCalendarIconExpandedState(isOpen);

    // Управление backdrop на мобильных устройствах
    const backdrop = this.instanseElement.querySelector('.dtp-backdrop') as HTMLElement;

    if (isOpen) {
      this.setDatetimeStep('date');
      this.timeChangedByUserDuringDateStep = false;
      this.setTimeGridOpen(false);
      this.ensureTimeModeSelectedDate();
      this.setShowingDate(this.resolveInitialShowingDate());
      this.changeDate();

      // При открытии пикера устанавливаем фокус на текущую или выбранную дату
      this.initializeFocus();
      if (this.mode === 'month') {
        this.setPickerView('month');
      }
      if (this.mode === 'time') {
        this.setTimeGridOpen(false);
      }
      this.emit('open', { picker: this });

      this.positionPicker();

      // Блокировка скролла body только для мобильного modal-режима
      if (this.isMobileViewport()) {
        // Блокировка скролла body на мобильных устройствах
        this.lockBody();

        // Показать backdrop
        if (backdrop) {
          backdrop.style.display = 'block';
          // Задержка для CSS transition
          requestAnimationFrame(() => {
            if (!this.destroyed && this.isPickerOpen)
              backdrop.classList.add('dtp-backdrop--visible');
          });
        }
      } else {
        if (backdrop) {
          backdrop.classList.remove('dtp-backdrop--visible');
          backdrop.style.display = '';
        }
      }
    } else {
      // Сбрасываем состояние пикера
      this.setDatetimeStep('date');
      this.resetFocus();
      this.emit('close', { picker: this });
      this.setPickerView('calendar');

      this.unlockBody();

      // Скрываем backdrop
      if (backdrop) {
        backdrop.classList.remove('dtp-backdrop--visible');
        backdrop.style.display = '';
      }
    }
  }

  /**
   * Сбросить все inline-стили пикера, чтобы CSS взял управление
   */
  protected resetPickerStyles(): void {
    const wrapper = this.getPickerWrapper();
    if (!(wrapper instanceof HTMLElement)) {
      return;
    }

    // Сбросить все inline-стили позиционирования
    wrapper.style.top = '';
    wrapper.style.bottom = '';
    wrapper.style.left = '';
    wrapper.style.right = '';
    wrapper.style.width = '';
    wrapper.style.maxWidth = '';
    wrapper.style.maxHeight = '';
    wrapper.style.overflowY = '';
    wrapper.style.transform = '';
    wrapper.style.position = '';

    // Удалить классы режимов позиционирования
    wrapper.classList.remove('dtp-adaptive');
    wrapper.classList.remove('dtp-mobile-modal');
    wrapper.classList.remove('dtp-anchored');
  }

  protected isMobileViewport(): boolean {
    return window.innerWidth < 768;
  }

  protected positionPicker(): void {
    if (this.isMobileViewport()) {
      this.positionPickerAsMobileModal();
      return;
    }

    this.positionPickerAsAnchoredPopup();
  }

  protected positionPickerAsMobileModal(): void {
    const wrapper = this.getPickerWrapper();
    if (!(wrapper instanceof HTMLElement)) {
      return;
    }

    this.resetPickerStyles();
    wrapper.classList.add('dtp-adaptive', 'dtp-mobile-modal');
  }

  protected positionPickerAsAnchoredPopup(): void {
    const wrapper = this.getPickerWrapper();
    const input = this.displayInputElement ?? this.inputElement;

    if (!(wrapper instanceof HTMLElement) || !(input instanceof HTMLElement)) {
      return;
    }

    this.resetPickerStyles();
    wrapper.classList.add('dtp-anchored');

    const MARGIN = 8;
    const inputRect = input.getBoundingClientRect();
    const hostRect = this.instanseElement.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const wrapperWidth = Math.min(wrapperRect.width || 338, viewportWidth - MARGIN * 2);
    const wrapperHeight = Math.min(wrapperRect.height || 360, viewportHeight - MARGIN * 2);
    const spaceBelow = viewportHeight - inputRect.bottom - MARGIN;
    const spaceAbove = inputRect.top - MARGIN;
    const shouldOpenAbove = spaceBelow < wrapperHeight && spaceAbove > spaceBelow;
    const availableHeight = Math.max(120, viewportHeight - MARGIN * 2);

    let desiredViewportTop: number;
    if (shouldOpenAbove) {
      desiredViewportTop = inputRect.top - wrapperHeight - MARGIN;
    } else {
      desiredViewportTop = inputRect.bottom + MARGIN;
    }
    const maxViewportTop = Math.max(MARGIN, viewportHeight - wrapperHeight - MARGIN);
    const clampedViewportTop = Math.max(MARGIN, Math.min(desiredViewportTop, maxViewportTop));
    const top = clampedViewportTop - hostRect.top;
    const effectiveMaxHeight = Math.max(
      120,
      Math.min(availableHeight, viewportHeight - clampedViewportTop - MARGIN),
    );

    const inputCenterX = inputRect.left + inputRect.width / 2;
    const desiredViewportLeft = inputCenterX - wrapperWidth / 2;
    const clampedViewportLeft = Math.max(
      MARGIN,
      Math.min(desiredViewportLeft, viewportWidth - wrapperWidth - MARGIN),
    );
    const left = clampedViewportLeft - hostRect.left;

    wrapper.style.position = 'absolute';
    wrapper.style.top = `${top}px`;
    wrapper.style.left = `${left}px`;
    wrapper.style.right = 'auto';
    wrapper.style.width = `${wrapperWidth}px`;
    wrapper.style.maxHeight = `${effectiveMaxHeight}px`;
    wrapper.style.transform = 'none';
    wrapper.style.overflowY = 'auto';
  }

  protected adjustShowingDateToSelected(): void {
    const selectedDate = this.getSelectedDateTime();
    if (!selectedDate) {
      return;
    }

    const currentComponents = getDateComponentsWithTimezone(
      this.showingDate,
      this.options.timezone,
    );
    const selectedComponents = getDateComponentsWithTimezone(selectedDate, this.options.timezone);

    // Если месяц или год отличаются, обновить showingDate и перерисовать календарь
    if (
      currentComponents.month !== selectedComponents.month ||
      currentComponents.year !== selectedComponents.year
    ) {
      this.setShowingDate(selectedDate);
      this.changeDate();
    }
  }

  protected initializeFocus(): void {
    const targetDate = this.getSelectedDateTime() || this.today;
    const calendarBody = this.instanseElement.querySelector('.dtp-wrapper .dtp-calender tbody');
    if (!calendarBody) {
      this.focusedDayIndex = 0;
      this.updateDayFocus();
      return;
    }

    const allCells = calendarBody.querySelectorAll<HTMLTableCellElement>('td');
    let targetIndex = -1;

    // Искать дату в отрендеренных днях
    for (let i = 0; i < this.lastRenderedDays.length; i++) {
      const date = this.getDateForDayIndex(i, this.lastRenderedDays);
      if (isSameDayWithTimezone(date, targetDate, this.options.timezone)) {
        targetIndex = i;
        break;
      }
    }

    if (targetIndex === -1) {
      for (let i = 0; i < allCells.length; i++) {
        if (this.lastRenderedDays[i]?.isCurrentMonth && !this.isDayCellDisabled(allCells[i])) {
          targetIndex = i;
          break;
        }
      }
    }

    if (targetIndex === -1) {
      for (let i = 0; i < allCells.length; i++) {
        if (!this.isDayCellDisabled(allCells[i])) {
          targetIndex = i;
          break;
        }
      }
    }

    if (targetIndex === -1) {
      targetIndex = 0;
    }

    this.focusedDayIndex = targetIndex;
    this.updateDayFocus();
  }

  protected setPickerView(view: 'calendar' | 'month' | 'year'): void {
    const previousView = this.pickerView;
    this.pickerView = view;

    // Сбросить фокус при смене вида
    this.resetFocus();

    // Emit viewChange event if view actually changed
    if (view !== previousView) {
      this.emit('viewChange', {
        view,
        previousView,
        picker: this,
      });
    }

    this.syncPickerSections();
  }

  protected updateRangeEndInputVisibility(): void {
    if (this.options.workType !== WORK_TYPE.MULTI) {
      return;
    }

    if (!(this.rangeEndInputElement instanceof HTMLInputElement)) {
      return;
    }

    const rangeEndVisibleInput = this.rangeEndDisplayInputElement ?? this.rangeEndInputElement;
    rangeEndVisibleInput.hidden = this.rangeEndInputElement.value.trim() === '';
  }

  protected renderMonthPicker(): void {
    const monthGrid = this.instanseElement.querySelector('.dtp-month-picker-grid');
    if (!(monthGrid instanceof HTMLElement)) {
      return;
    }

    const showingComponents = getDateComponentsWithTimezone(
      this.showingDate,
      this.options.timezone,
    );
    const monthButtons = Array.from({ length: 12 }, (_, month) => {
      const monthKey = this.month.getKey(month);
      const monthTitle = this.t(monthKey);
      const activeClass = month === showingComponents.month ? 'active' : '';
      const disabled =
        this.mode === 'month' &&
        !this.isDateValid(
          createDateWithTimezone(showingComponents.year, month, 1, this.options.timezone),
        );
      return `<button type="button" ${disabled ? 'disabled aria-disabled="true"' : ''} class="dtp-month-item ${activeClass}" data-month="${month}">${monthTitle}</button>`;
    }).join('');

    monthGrid.innerHTML = monthButtons;
  }

  protected renderYearPicker(): void {
    const yearGrid = this.instanseElement.querySelector('.dtp-year-picker-grid');
    const yearRange = this.instanseElement.querySelector('.dtp-year-range');
    if (!(yearGrid instanceof HTMLElement) || !(yearRange instanceof HTMLElement)) {
      return;
    }

    const start = this.yearPickerStart;
    const end = start + 11;
    yearRange.innerText = `${start} - ${end}`;
    const showingComponents = getDateComponentsWithTimezone(
      this.showingDate,
      this.options.timezone,
    );

    const yearButtons = Array.from({ length: 12 }, (_, idx) => start + idx)
      .map(year => {
        const activeClass = year === showingComponents.year ? 'active' : '';
        return `<button type="button" class="dtp-year-item ${activeClass}" data-year="${year}">${year}</button>`;
      })
      .join('');

    yearGrid.innerHTML = yearButtons;
  }

  protected t(key: string, fallback?: string): string {
    const locale = this.i18n.getLocale();
    return locale[key] ?? fallback ?? key;
  }

  protected getDisplayInputValidationMessage(): string {
    return this.t('invalid date', 'Enter a valid date');
  }

  protected clearDisplayInputValidity(displayInput: HTMLInputElement): void {
    displayInput.removeAttribute('aria-invalid');
    displayInput.setCustomValidity('');
  }

  protected setDisplayInputPending(displayInput: HTMLInputElement): void {
    displayInput.removeAttribute('aria-invalid');
    displayInput.setCustomValidity(this.getDisplayInputValidationMessage());
  }

  protected setDisplayInputInvalid(displayInput: HTMLInputElement): void {
    displayInput.setAttribute('aria-invalid', 'true');
    displayInput.setCustomValidity(this.getDisplayInputValidationMessage());
  }

  injectTemplate(): void {
    const template = document.createElement('template');
    template.innerHTML = generateHtmlTemplate(k => this.t(k));
    this.instanseElement.appendChild(template.content.cloneNode(true));

    // Устанавливаем ID для wrapper элемента (для accessibility)
    const wrapper = this.getPickerWrapper();
    if (wrapper) {
      wrapper.id = this.dialogId;
    }
  }

  protected prepareInstanceElement(element: HTMLElement): HTMLElement {
    if (element instanceof HTMLInputElement) {
      this.inputElement = element;

      const existingHost = element.closest('.dtp-host');
      if (existingHost instanceof HTMLElement) {
        this.displayInputElement = this.markManagedInput(this.inputElement);
        return existingHost;
      }

      // Создаем контейнер, который обернет input
      const host = document.createElement('div');
      host.className = 'dtp-host';
      this.createdHostElement = true;

      // Вставляем host перед input, затем перемещаем input внутрь host
      element.parentNode?.insertBefore(host, element);
      host.appendChild(element);

      this.displayInputElement = this.markManagedInput(this.inputElement);

      return host;
    }

    const input = element.querySelector(
      'input[type="date"], input[type="datetime-local"], input[type="month"], input[type="time"]',
    );
    if (input instanceof HTMLInputElement) {
      this.inputElement = input;
      this.displayInputElement = this.markManagedInput(this.inputElement);
    }

    return element;
  }

  protected initSelectedDateFromInput(): void {
    if (!(this.inputElement instanceof HTMLInputElement)) {
      return;
    }

    if (this.options.workType === WORK_TYPE.MULTI) {
      const range = this.parseRangeInputValue();
      if (range.length > 0) {
        this.selectedDate = range;
        this.appliedDate = range.map(date => new Date(date.getTime()));
        this.selectedTime = this.getTimeValue(range[0]);
        this.appliedTime = this.selectedTime;
      }

      return;
    }

    if (this.inputElement.value === '') {
      return;
    }

    const parsed = this.parseValueInput(this.inputElement, this.displayInputElement);
    if (!(parsed instanceof Date)) {
      return;
    }

    this.selectedDate = [parsed];
    this.appliedDate = [new Date(parsed.getTime())];
    this.selectedTime = this.getTimeValue(parsed);
    this.appliedTime = this.selectedTime;
  }

  protected parseRangeInputValue(): Date[] {
    if (!(this.inputElement instanceof HTMLInputElement)) {
      return [];
    }

    if (this.rangeEndInputElement instanceof HTMLInputElement) {
      const start = this.parseValueInput(this.inputElement, this.displayInputElement);
      const end = this.parseValueInput(this.rangeEndInputElement, this.rangeEndDisplayInputElement);
      const result = [start, end].filter((item): item is Date => item instanceof Date);
      if (result.length <= 1) {
        return result;
      }

      const sorted = result.sort((a, b) => a.getTime() - b.getTime());
      return [sorted[0], sorted[1]];
    }

    const valueParts = this.splitRangeValue(this.inputElement.value);
    const displayParts = this.splitRangeValue(this.displayInputElement?.value ?? '');
    const inputType = this.getManagedInputType(this.inputElement);
    const partCount = Math.max(valueParts.length, displayParts.length);

    const parsed = Array.from({ length: partCount }, (_, index) => {
      const parsedValue = this.parseValueInputValue(valueParts[index] ?? '', inputType);
      if (parsedValue instanceof Date) {
        return parsedValue;
      }

      return this.parseInputValue(displayParts[index] ?? '', 'text');
    }).filter((item): item is Date => item instanceof Date);

    if (parsed.length <= 1) {
      return parsed;
    }

    const sorted = parsed.sort((a, b) => a.getTime() - b.getTime());
    return [sorted[0], sorted[1]];
  }

  protected splitRangeValue(value: string): string[] {
    const trimmed = value.trim();
    if (trimmed === '') {
      return [];
    }

    return trimmed.includes(this.options.rangeSeparator)
      ? trimmed.split(this.options.rangeSeparator)
      : [trimmed];
  }

  protected setShowingDate(date?: Date): void {
    this.showingDate = date ?? this.today;
  }

  protected parseValueInput(
    valueInput: HTMLInputElement,
    displayInput?: HTMLInputElement | null,
  ): Date | null {
    const inputType = this.getManagedInputType(valueInput);
    const parsedValue = this.parseValueInputValue(valueInput.value, inputType);
    if (parsedValue instanceof Date) {
      return parsedValue;
    }

    if (displayInput instanceof HTMLInputElement) {
      return this.parseInputValue(displayInput.value, 'text');
    }

    return null;
  }

  protected parseValueInputValue(value: string, inputType: string): Date | null {
    const normalizedValue = value.trim();
    if (normalizedValue === '') {
      return null;
    }

    let fallbackDate = this.today;
    if (inputType === 'month') {
      const todayComponents = getDateComponentsWithTimezone(this.today, this.options.timezone);
      fallbackDate = createDateWithTimezone(
        todayComponents.year,
        todayComponents.month,
        1,
        this.options.timezone,
      );
    }
    const parsedByValueFormat = parseDateByPattern(
      normalizedValue,
      this.resolveValueFormat(inputType),
      this.options.timezone,
      fallbackDate,
    );
    if (parsedByValueFormat instanceof Date) {
      return parsedByValueFormat;
    }

    return this.parseInputValue(normalizedValue, inputType);
  }

  protected parseInputValue(value: string, inputType: string): Date | null {
    const normalizedValue = value.trim();
    if (normalizedValue === '') {
      return null;
    }

    if (inputType === 'text') {
      const parsedByPattern = parseDateByPattern(
        normalizedValue,
        this.resolveDisplayFormat(),
        this.options.timezone,
        this.today,
      );
      if (parsedByPattern instanceof Date) {
        return parsedByPattern;
      }

      const nativeTypeCandidates = ['datetime-local', 'date', 'month', 'time'] as const;
      for (const nativeType of nativeTypeCandidates) {
        const parsedNativeValue = this.parseInputValue(normalizedValue, nativeType);
        if (parsedNativeValue instanceof Date) {
          return parsedNativeValue;
        }
      }

      return null;
    }

    if (inputType === 'date') {
      const match = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!match) {
        return null;
      }

      const parsed = createDateWithTimezone(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        this.options.timezone,
      );
      return this.matchesDateComponents(
        parsed,
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
      )
        ? parsed
        : null;
    }

    if (inputType === 'month') {
      const match = normalizedValue.match(/^(\d{4})-(\d{2})$/);
      if (!match) {
        return null;
      }

      const parsed = createDateWithTimezone(
        Number(match[1]),
        Number(match[2]) - 1,
        1,
        this.options.timezone,
      );
      return this.matchesDateComponents(parsed, Number(match[1]), Number(match[2]) - 1, 1)
        ? parsed
        : null;
    }

    if (inputType === 'datetime-local') {
      const match = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
      if (!match) {
        return null;
      }

      const parsed = createDateTimeWithTimezone(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
        this.options.timezone,
      );
      return this.matchesDateComponents(
        parsed,
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
      )
        ? parsed
        : null;
    }

    if (inputType === 'time') {
      const match = normalizedValue.match(/^(\d{2}):(\d{2})$/);
      if (!match) {
        return null;
      }

      // Time inputs use timezone-aware date and time creation on current date
      const todayComponents = getDateComponentsWithTimezone(this.today, this.options.timezone);
      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      const parsed = createDateTimeWithTimezone(
        todayComponents.year,
        todayComponents.month,
        todayComponents.day,
        hours,
        minutes,
        this.options.timezone,
      );
      return this.matchesDateComponents(
        parsed,
        todayComponents.year,
        todayComponents.month,
        todayComponents.day,
        hours,
        minutes,
      )
        ? parsed
        : null;
    }

    return null;
  }

  protected matchesDateComponents(
    date: Date,
    year: number,
    month: number,
    day: number,
    hours?: number,
    minutes?: number,
  ): boolean {
    const components = getDateComponentsWithTimezone(date, this.options.timezone);
    return (
      components.year === year &&
      components.month === month &&
      components.day === day &&
      (typeof hours === 'undefined' || components.hours === hours) &&
      (typeof minutes === 'undefined' || components.minutes === minutes)
    );
  }

  protected pad2(value: number): string {
    return value.toString().padStart(2, '0');
  }

  protected formatInputValue(date: Date, inputType: string): string {
    return formatDateByPattern(date, this.resolveValueFormat(inputType), this.options.timezone);
  }

  protected setManagedInputDateValue(
    valueInput: HTMLInputElement,
    displayInput: HTMLInputElement | null,
    date: Date | null,
  ): string {
    if (!(date instanceof Date)) {
      valueInput.value = '';
      if (displayInput instanceof HTMLInputElement) {
        displayInput.value = '';
        this.clearDisplayInputValidity(displayInput);
      }
      return '';
    }

    const value = this.formatInputValue(date, this.getManagedInputType(valueInput));
    valueInput.value = value;
    if (displayInput instanceof HTMLInputElement) {
      displayInput.value = this.formatDisplayDate(date);
      this.clearDisplayInputValidity(displayInput);
    }

    return value;
  }

  protected applySelectedDateToInput(): void {
    if (!(this.inputElement instanceof HTMLInputElement)) {
      return;
    }
    const startInput = this.inputElement;

    const selectedPayload = this.getSelectedValuePayload();
    let emittedValue = '';
    if (selectedPayload === null) {
      this.setManagedInputDateValue(startInput, this.displayInputElement, null);
      if (this.rangeEndInputElement instanceof HTMLInputElement) {
        this.setManagedInputDateValue(
          this.rangeEndInputElement,
          this.rangeEndDisplayInputElement,
          null,
        );
      }
    } else if (Array.isArray(selectedPayload)) {
      if (this.rangeEndInputElement instanceof HTMLInputElement) {
        const start = selectedPayload[0];
        const end = selectedPayload[1];
        const startValue = this.setManagedInputDateValue(
          startInput,
          this.displayInputElement,
          start,
        );
        const endValue = this.setManagedInputDateValue(
          this.rangeEndInputElement,
          this.rangeEndDisplayInputElement,
          end instanceof Date ? end : null,
        );
        emittedValue = [startValue, endValue]
          .filter(part => part !== '')
          .join(this.options.rangeSeparator);
      } else {
        const valueParts = selectedPayload.map(date =>
          this.formatInputValue(date, this.getManagedInputType(startInput)),
        );
        const displayParts = selectedPayload.map(date => this.formatDisplayDate(date));
        startInput.value = valueParts.join(this.options.rangeSeparator);
        if (this.displayInputElement instanceof HTMLInputElement) {
          this.displayInputElement.value = displayParts.join(this.options.rangeSeparator);
          this.clearDisplayInputValidity(this.displayInputElement);
        }
        emittedValue = startInput.value;
      }
    } else {
      emittedValue = this.setManagedInputDateValue(
        startInput,
        this.displayInputElement,
        selectedPayload,
      );
    }

    this.dispatchInputEvents(startInput, this.displayInputElement, 'input');
    this.options.onInput?.(emittedValue, selectedPayload, this);
    this.dispatchInputEvents(startInput, this.displayInputElement, 'change');
    this.options.onChange?.(emittedValue, selectedPayload, this);

    if (this.rangeEndInputElement instanceof HTMLInputElement) {
      this.dispatchInputEvents(
        this.rangeEndInputElement,
        this.rangeEndDisplayInputElement,
        'input',
      );
      this.dispatchInputEvents(
        this.rangeEndInputElement,
        this.rangeEndDisplayInputElement,
        'change',
      );
    }
    this.updateRangeEndInputVisibility();
  }

  protected commitSelectedDateToInput(allowPreventClose: boolean = false): void {
    const payload = this.getSelectedValuePayload();
    if (payload && !this.isSelectionValid(Array.isArray(payload) ? payload : [payload])) return;
    this.appliedDate = this.selectedDate.map(date => new Date(date.getTime()));
    this.appliedTime = this.selectedTime;

    const selectedValue = this.getSelectedValuePayload();
    if (selectedValue !== null) {
      this.emit('select', {
        date: selectedValue,
        picker: this,
      });
    }

    this.applySelectedDateToInput();
    const shouldClose =
      !allowPreventClose || selectedValue === null
        ? true
        : (this.options.shouldCloseOnSelect?.(selectedValue, this) ?? true);
    if (shouldClose) {
      this.setPickerOpen(false);
    }
  }

  protected shouldCommitSelectedDateOnSelect(): boolean {
    if (!this.options.commitOnSelect) {
      return false;
    }

    if (this.options.workType === WORK_TYPE.MULTI && this.selectedDate.length < 2) {
      return false;
    }

    if (this.shouldShowDatetimeTimeStepOnSelect()) {
      return false;
    }

    return this.getSelectedValuePayload() !== null;
  }

  protected shouldShowDatetimeTimeStepOnSelect(): boolean {
    return (
      this.options.commitOnSelect &&
      DateTimePicker.DATETIME_MODE_ALIASES.has(this.mode) &&
      this.options.workType === WORK_TYPE.SINGLE &&
      this.datetimeStep === 'date' &&
      !this.timeChangedByUserDuringDateStep &&
      this.selectedDate.length > 0
    );
  }

  protected setDatetimeStep(step: 'date' | 'time', options: { openTimeGrid?: boolean } = {}): void {
    const nextStep = DateTimePicker.DATETIME_MODE_ALIASES.has(this.mode) ? step : 'date';
    this.datetimeStep = nextStep;
    this.syncPickerSections();

    if (nextStep === 'time') {
      this.setTimeGridOpen(options.openTimeGrid ?? false);
      this.focusTimeManualInput();
    }
  }

  protected dispatchInputEvents(
    valueInput: HTMLInputElement,
    displayInput: HTMLInputElement | null,
    eventType: 'input' | 'change',
  ): void {
    valueInput.dispatchEvent(new Event(eventType, { bubbles: true }));

    if (displayInput instanceof HTMLInputElement && displayInput !== valueInput) {
      displayInput.dispatchEvent(new Event(eventType, { bubbles: true }));
    }
  }

  protected syncSelectedDateToInputs(): void {
    if (!(this.inputElement instanceof HTMLInputElement)) {
      return;
    }

    const startInput = this.inputElement;
    const selectedPayload = this.getSelectedValuePayload();

    if (selectedPayload === null) {
      this.setManagedInputDateValue(startInput, this.displayInputElement, null);
      if (this.rangeEndInputElement instanceof HTMLInputElement) {
        this.setManagedInputDateValue(
          this.rangeEndInputElement,
          this.rangeEndDisplayInputElement,
          null,
        );
      }
      return;
    }

    if (Array.isArray(selectedPayload)) {
      if (this.rangeEndInputElement instanceof HTMLInputElement) {
        const start = selectedPayload[0];
        const end = selectedPayload[1];
        this.setManagedInputDateValue(startInput, this.displayInputElement, start);
        this.setManagedInputDateValue(
          this.rangeEndInputElement,
          this.rangeEndDisplayInputElement,
          end instanceof Date ? end : null,
        );
        return;
      }

      const valueParts = selectedPayload.map(date =>
        this.formatInputValue(date, this.getManagedInputType(startInput)),
      );
      const displayParts = selectedPayload.map(date => this.formatDisplayDate(date));
      startInput.value = valueParts.join(this.options.rangeSeparator);
      if (this.displayInputElement instanceof HTMLInputElement) {
        this.displayInputElement.value = displayParts.join(this.options.rangeSeparator);
      }
      return;
    }

    this.setManagedInputDateValue(startInput, this.displayInputElement, selectedPayload);
  }

  protected formatDisplayDate(date: Date): string {
    return formatDateByPattern(date, this.resolveDisplayFormat(), this.options.timezone);
  }

  protected updateSelectedDateLabel(): void {
    const selectedDateElement = this.instanseElement.querySelector('.dtp-selected-date');
    if (!(selectedDateElement instanceof HTMLElement)) {
      return;
    }

    if (this.options.workType === WORK_TYPE.MULTI) {
      if (this.selectedDate.length === 0) {
        selectedDateElement.innerText = '';
        return;
      }

      const [start, end] = this.getRangeStartEnd();
      if (!(start instanceof Date)) {
        selectedDateElement.innerText = '';
        return;
      }

      if (!(end instanceof Date)) {
        selectedDateElement.innerText = this.formatDisplayDate(this.getDateWithSelectedTime(start));
        return;
      }

      selectedDateElement.innerText = `${this.formatDisplayDate(this.getDateWithSelectedTime(start))}${this.options.rangeSeparator}${this.formatDisplayDate(this.getDateWithSelectedTime(end))}`;
      return;
    }

    const selected = this.getSelectedDateTime();
    selectedDateElement.innerText =
      selected instanceof Date ? this.formatDisplayDate(selected) : '';
  }

  protected setDayHeader(date?: Date): void {
    date = date ?? this.showingDate;
    const { year, month: monthNumber } = getDateComponentsWithTimezone(date, this.options.timezone);

    const month = this.month.getKey(monthNumber);

    const dayHeader = this.instanseElement.querySelector('.dtp-month-and-year-header');
    if (dayHeader !== null) {
      const monthElement = dayHeader.querySelector('.month');
      const yearElement = dayHeader.querySelector('.year');
      if (monthElement !== null) {
        monthElement.innerHTML = this.t(month);
      }
      if (yearElement !== null) {
        yearElement.innerHTML = year.toString();
      }
    }
  }

  protected getFirstDayMonth(date: Date): Date {
    // Используем timezone-aware создание даты
    const components = getDateComponentsWithTimezone(date, this.options.timezone);
    return createDateWithTimezone(components.year, components.month, 1, this.options.timezone);
  }

  protected getLastDayMonth(date: Date): Date {
    const components = getDateComponentsWithTimezone(date, this.options.timezone);
    return createDateWithTimezone(components.year, components.month + 1, 0, this.options.timezone);
  }

  protected getAvailableMonth(date?: Date): CalendarDay[] {
    date = date ?? this.showingDate;

    let prevMonthDays = this.getPreviousDays(date);
    if (prevMonthDays.length >= 11) {
      prevMonthDays = prevMonthDays.slice(7, prevMonthDays.length);
    }

    const firstDayMonth: Date = this.getFirstDayMonth(date);
    const lastDayMonth = this.getLastDayMonth(date);

    const params = {
      startDate: firstDayMonth,
      endDate: lastDayMonth,
      todayDate: this.today,
      isCurrentMonth: true,
      selectedDate: this.getCalendarSelectedDates(),
      workType: this.options.workType,
      timezone: this.options.timezone,
    } as ICalendarDayMake;
    const availableMpnthDays = CalendarDay.make(params);

    const result = prevMonthDays.concat(availableMpnthDays);

    if (result.length < 42) {
      const nextMonthDays = this.getNextDays(42 - result.length, date);
      return result.concat(nextMonthDays);
    }

    return result;
  }

  protected setCalendarHeader(): void {
    const res = this.weekDays.getHeaderDays();
    const elements = this.instanseElement.querySelectorAll('.dtp-wrapper .dtp-calender thead th');
    for (let i = 0; i <= elements.length - 1; i++) {
      const name = res[i];
      elements[i].innerHTML = this.t(name);
    }
  }

  /**
   * Форматирует дату для ARIA-атрибутов в читаемый формат
   */
  protected formatDateForAria(date: Date): string {
    const formatter = new Intl.DateTimeFormat(this.options.lang, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone:
        this.options.timezone === 'utc'
          ? 'UTC'
          : typeof this.options.timezone === 'string' && this.options.timezone !== 'local'
            ? this.options.timezone
            : undefined,
    });
    return formatter.format(date);
  }

  /**
   * Вычисляет дату для дня календаря по его индексу в сетке
   * @param index Индекс дня в сетке календаря (0-41)
   * @param days Массив дней календаря
   * @returns Дата для этого дня
   */
  protected getDateForDayIndex(index: number, days: CalendarDay[]): Date {
    const showingComponents = getDateComponentsWithTimezone(
      this.showingDate,
      this.options.timezone,
    );
    const calendarDay = days[index];
    if (!calendarDay) {
      return createDateWithTimezone(
        showingComponents.year,
        showingComponents.month,
        1,
        this.options.timezone,
      );
    }

    // Находим первый день текущего месяца в массиве
    let firstCurrentMonthIndex = -1;
    for (let i = 0; i < days.length; i++) {
      if (days[i].isCurrentMonth) {
        firstCurrentMonthIndex = i;
        break;
      }
    }

    if (firstCurrentMonthIndex === -1) {
      return createDateWithTimezone(
        showingComponents.year,
        showingComponents.month,
        calendarDay.day,
        this.options.timezone,
      );
    }

    if (index < firstCurrentMonthIndex) {
      const prevMonthDate = createDateWithTimezone(
        showingComponents.year,
        showingComponents.month - 1,
        1,
        this.options.timezone,
      );
      const prevMonthComponents = getDateComponentsWithTimezone(
        prevMonthDate,
        this.options.timezone,
      );
      return createDateWithTimezone(
        prevMonthComponents.year,
        prevMonthComponents.month,
        calendarDay.day,
        this.options.timezone,
      );
    }

    // Находим последний день текущего месяца в массиве
    let lastCurrentMonthIndex = -1;
    for (let i = days.length - 1; i >= 0; i--) {
      if (days[i].isCurrentMonth) {
        lastCurrentMonthIndex = i;
        break;
      }
    }

    if (index > lastCurrentMonthIndex) {
      const nextMonthDate = createDateWithTimezone(
        showingComponents.year,
        showingComponents.month + 1,
        1,
        this.options.timezone,
      );
      const nextMonthComponents = getDateComponentsWithTimezone(
        nextMonthDate,
        this.options.timezone,
      );
      return createDateWithTimezone(
        nextMonthComponents.year,
        nextMonthComponents.month,
        calendarDay.day,
        this.options.timezone,
      );
    }

    return createDateWithTimezone(
      showingComponents.year,
      showingComponents.month,
      calendarDay.day,
      this.options.timezone,
    );
  }

  protected setCalendarDays(days: CalendarDay[]): void {
    const elements = this.instanseElement.querySelectorAll('.dtp-wrapper .dtp-calender tbody td');
    for (let i = 0; i <= elements.length - 1; i++) {
      const calendarDay = days[i];
      const element = elements[i];
      element.innerHTML = calendarDay.day.toString();

      element.classList.remove('current', 'disable', 'today', 'active', 'disabled');

      // Сбросить ARIA атрибуты
      element.removeAttribute('aria-selected');
      element.removeAttribute('aria-current');
      element.removeAttribute('aria-disabled');
      element.removeAttribute('aria-label');

      // Вычислить дату для этого дня
      const dateForDay = this.getDateForDayIndex(i, days);
      const isValid = this.isDateValid(dateForDay);

      if (calendarDay.isCurrentMonth) {
        element.classList.add('current');
        // Для дней текущего месяца добавляем aria-label с номером дня
        element.setAttribute('aria-label', `${calendarDay.day}`);
      } else {
        element.classList.add('disable');
        element.setAttribute('aria-disabled', 'true');
        // Для дней других месяцев добавляем уточнение
        const monthType = calendarDay.day > 15 ? 'previous' : 'next';
        element.setAttribute('aria-label', `${calendarDay.day} (${monthType} month)`);
      }

      if (!isValid) {
        element.classList.add('disabled');
        element.setAttribute('aria-disabled', 'true');
      }

      if (calendarDay.isToday) {
        element.classList.add('today');
        element.setAttribute('aria-current', 'date');
      }

      if (calendarDay.isActive) {
        element.classList.add('active');
        element.setAttribute('aria-selected', 'true');
      }
    }

    // Сохраняем отрендеренные дни для keyboard navigation
    this.lastRenderedDays = days;
  }

  protected getPreviousDays(currentDate?: Date): ICalendarDay[] {
    currentDate = currentDate ?? this.showingDate;
    const { year: currentYear, month: currentMonth } = getDateComponentsWithTimezone(
      currentDate,
      this.options.timezone,
    );
    const firstDayMonth = this.getFirstDayMonth(currentDate);
    const firstDay = getWeekdayWithTimezone(firstDayMonth, this.options.timezone);
    const lastDayMonth = this.getLastDayMonth(currentDate);
    const { day: lastDate } = getDateComponentsWithTimezone(lastDayMonth, this.options.timezone);

    // Количество недель, которые нужно дбавить вначало
    const needPrevWeek = 6 - Math.ceil(lastDate / 7); // 6 - недель отображаемых в календаре; 7 - колиичество дней в неделе

    // Получить количество дней для первой недели текущего месяца
    const fwCountDayPm = this.weekDays.getPosition(firstDay);

    // Получаем количество дней, которое нужно получить от прошлого месяца
    const countDaysFromPrevMonth = needPrevWeek * 7 + fwCountDayPm; // 7 - количество дней в неделе

    const prevMonthDate = createDateWithTimezone(
      currentYear,
      currentMonth,
      0,
      this.options.timezone,
    );
    const prevMonthComponents = getDateComponentsWithTimezone(prevMonthDate, this.options.timezone);
    const prevMonthDays = prevMonthComponents.day;
    const curDayPrevMonth = prevMonthDays - countDaysFromPrevMonth + 1; // +1 нужно для того, что бы получить корректную дату начала.

    const startDate = createDateWithTimezone(
      prevMonthComponents.year,
      prevMonthComponents.month,
      curDayPrevMonth,
      this.options.timezone,
    );

    const params = {
      startDate: startDate,
      endDate: prevMonthDate,
      todayDate: this.today,
      selectedDate: this.getCalendarSelectedDates(),
      isCurrentMonth: false,
      workType: this.options.workType,
      timezone: this.options.timezone,
    } as ICalendarDayMake;
    return CalendarDay.make(params);
  }

  protected getNextDays(needCountDays: number, currentDate?: Date): ICalendarDay[] {
    currentDate = currentDate ?? this.showingDate;

    if (needCountDays <= 0) {
      return [];
    }

    const currentComponents = getDateComponentsWithTimezone(currentDate, this.options.timezone);
    const nextMonth = createDateWithTimezone(
      currentComponents.year,
      currentComponents.month + 1,
      1,
      this.options.timezone,
    );
    const nextMonthComponents = getDateComponentsWithTimezone(nextMonth, this.options.timezone);
    const endDate = createDateWithTimezone(
      nextMonthComponents.year,
      nextMonthComponents.month,
      needCountDays,
      this.options.timezone,
    );

    return CalendarDay.make({
      startDate: nextMonth,
      endDate: endDate,
      todayDate: this.today,
      selectedDate: this.getCalendarSelectedDates(),
      isCurrentMonth: false,
      workType: this.options.workType,
      timezone: this.options.timezone,
    });
  }

  protected getCalendarSelectedDates(): Date[] {
    if (
      this.options.workType !== WORK_TYPE.MULTI ||
      this.selectedDate.length !== 1 ||
      !(this.rangePreviewDate instanceof Date)
    ) {
      return this.selectedDate;
    }

    const start = this.selectedDate[0];
    return this.rangePreviewDate.getTime() < start.getTime()
      ? [this.rangePreviewDate, start]
      : [start, this.rangePreviewDate];
  }

  public changeDate(year?: number, month?: number): void {
    const currentComponents = getDateComponentsWithTimezone(
      this.showingDate,
      this.options.timezone,
    );

    year = year ?? currentComponents.year;
    month = month ?? currentComponents.month;
    const nextShowingDate = createDateWithTimezone(year, month, 1, this.options.timezone);
    const nextComponents = getDateComponentsWithTimezone(nextShowingDate, this.options.timezone);

    if (nextComponents.month !== this.previousMonth) {
      this.emit('monthChange', {
        month: nextComponents.month,
        year: nextComponents.year,
        picker: this,
      });
      this.previousMonth = nextComponents.month;
    }

    if (nextComponents.year !== this.previousYear) {
      this.emit('yearChange', {
        year: nextComponents.year,
        month: nextComponents.month,
        picker: this,
      });
      this.previousYear = nextComponents.year;
    }

    this.showingDate = nextShowingDate;

    this.setDayHeader();
    const res = this.getAvailableMonth();
    this.setCalendarDays(res);
    this.updateSelectedDateLabel();
    this.renderMonthPicker();
    this.renderYearPicker();
  }

  protected addEventListeners(): void {
    const calendarBody = this.instanseElement.querySelector('.dtp-wrapper .dtp-calender tbody');
    const openPicker = () => {
      this.setPickerOpen(true);
    };

    if (this.mode !== 'time') {
      // При manualInput: true не открываем пикер по фокусу/клику на поле,
      // чтобы пользователь мог ввести дату вручную
      if (!this.options.manualInput) {
        this.displayInputElement?.addEventListener('focus', openPicker);
        this.displayInputElement?.addEventListener('click', openPicker);
        this.rangeEndDisplayInputElement?.addEventListener('focus', openPicker);
        this.rangeEndDisplayInputElement?.addEventListener('click', openPicker);
      }
    }
    this.addManualInputListeners();

    const outsideMouseDown = (event: MouseEvent) => {
      if (!this.isPickerOpen) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      const wrapper = this.getPickerWrapper();
      const clickedInPicker = wrapper?.contains(target) ?? false;
      const clickedMainInput = this.inputElement?.contains(target) ?? false;
      const clickedMainDisplayInput = this.displayInputElement?.contains(target) ?? false;
      const clickedRangeEndInput = this.rangeEndInputElement?.contains(target) ?? false;
      const clickedRangeEndDisplayInput =
        this.rangeEndDisplayInputElement?.contains(target) ?? false;

      if (
        clickedInPicker ||
        clickedMainInput ||
        clickedMainDisplayInput ||
        clickedRangeEndInput ||
        clickedRangeEndDisplayInput
      ) {
        return;
      }

      this.setPickerOpen(false);
    };
    document.addEventListener('mousedown', outsideMouseDown);
    this.cleanupHandlers.push(() => document.removeEventListener('mousedown', outsideMouseDown));

    calendarBody?.addEventListener('click', (event: Event): void => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const cell = target.closest('td');

      if (!(cell instanceof HTMLTableCellElement)) {
        return;
      }

      // Установить фокус на кликнутую ячейку (даже если это не текущий месяц)
      const allCells = calendarBody.querySelectorAll<HTMLTableCellElement>('td');
      let cellIndex = -1;
      for (let i = 0; i < allCells.length; i++) {
        if (allCells[i] === cell) {
          cellIndex = i;
          break;
        }
      }

      if (cellIndex >= 0) {
        this.focusedDayIndex = cellIndex;
        this.updateDayFocus();
      }

      // Если кликнули на день не текущего месяца или disabled-день, не выбирать его
      if (!cell.classList.contains('current') || this.isDayCellDisabled(cell)) {
        return;
      }

      const day = Number(cell.textContent);
      if (!Number.isFinite(day)) {
        return;
      }

      if (this.options.workType === WORK_TYPE.SINGLE) {
        this.rangePreviewDate = null;
        const showingComponents = getDateComponentsWithTimezone(
          this.showingDate,
          this.options.timezone,
        );
        this.selectedDate = [
          createDateWithTimezone(
            showingComponents.year,
            showingComponents.month,
            day,
            this.options.timezone,
          ),
        ];

        // Emit select event for single date selection
        this.emit('select', {
          date: this.selectedDate[0],
          picker: this,
        });

        this.changeDate();
        if (this.shouldShowDatetimeTimeStepOnSelect()) {
          this.setDatetimeStep('time', { openTimeGrid: true });
        } else if (this.shouldCommitSelectedDateOnSelect()) {
          this.commitSelectedDateToInput(true);
        }
      }
      if (this.options.workType === WORK_TYPE.MULTI) {
        const showingComponents = getDateComponentsWithTimezone(
          this.showingDate,
          this.options.timezone,
        );
        const selected = createDateWithTimezone(
          showingComponents.year,
          showingComponents.month,
          day,
          this.options.timezone,
        );

        if (this.selectedDate.length === 0 || this.selectedDate.length >= 2) {
          this.selectedDate = [selected];
          this.rangePreviewDate = null;
          // Emit select event for first date in range
          this.emit('select', {
            date: selected,
            picker: this,
          });
        } else {
          const start = this.selectedDate[0];
          if (!this.isSelectionValid([start, selected].sort((a, b) => a.getTime() - b.getTime())))
            return;
          if (selected.getTime() < start.getTime()) {
            this.selectedDate = [selected, start];
          } else {
            this.selectedDate = [start, selected];
          }
          this.rangePreviewDate = null;
          // Emit select event for complete range
          this.emit('select', {
            date: this.selectedDate,
            picker: this,
          });
        }

        this.changeDate();
        if (this.shouldCommitSelectedDateOnSelect()) {
          this.commitSelectedDateToInput(true);
        }
      }
    });

    calendarBody?.addEventListener('mouseover', (event: Event): void => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const cell = target.closest('td');
      if (!(cell instanceof HTMLTableCellElement)) {
        return;
      }

      this.previewRangeToCell(cell, calendarBody);
    });

    calendarBody?.addEventListener('mouseleave', (): void => {
      this.clearRangePreview();
    });

    const timeSection = this.instanseElement.querySelector('.dtp-time-section');
    const timeManualInput = this.instanseElement.querySelector('.dtp-time-manual-input');
    if (timeManualInput instanceof HTMLInputElement) {
      timeManualInput.addEventListener('input', () => {
        this.formatTimeManualInput(timeManualInput);
        this.previewTimeManualInput(timeManualInput);
      });

      timeManualInput.addEventListener('blur', () => {
        this.applyTimeManualInput(timeManualInput);
      });

      timeManualInput.addEventListener('keydown', event => {
        if (event.key !== 'Enter') {
          return;
        }

        event.preventDefault();
        this.applyTimeManualInput(timeManualInput);
      });
    }

    timeSection?.querySelector('.dtp-time-toggle')?.addEventListener('click', event => {
      event.preventDefault();
      this.setTimeGridOpen(true);
      this.focusActiveTimeOption();
    });

    timeSection?.addEventListener('click', event => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const option = target.closest('.dtp-time-option');
      if (!(option instanceof HTMLButtonElement)) {
        return;
      }

      event.preventDefault();
      this.selectTimeOption(option);
    });

    timeSection?.addEventListener('keydown', event => {
      if (!(event instanceof KeyboardEvent)) {
        return;
      }

      if (!['Enter', ' ', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
        return;
      }

      const target = event.target;
      if (!(target instanceof HTMLButtonElement) || !target.classList.contains('dtp-time-option')) {
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.selectTimeOption(target);
        return;
      }

      const column = target.closest('.dtp-time-column');
      if (!(column instanceof HTMLElement)) {
        return;
      }

      const options = Array.from(column.querySelectorAll<HTMLButtonElement>('.dtp-time-option'));
      const currentIndex = options.indexOf(target);
      if (currentIndex < 0) {
        return;
      }

      event.preventDefault();
      const nextIndex =
        event.key === 'ArrowUp'
          ? Math.max(0, currentIndex - 1)
          : event.key === 'ArrowDown'
            ? Math.min(options.length - 1, currentIndex + 1)
            : event.key === 'Home'
              ? 0
              : options.length - 1;
      options[nextIndex]?.focus();
    });

    timeSection?.addEventListener('focusin', event => {
      const target = event.target;
      if (!(target instanceof HTMLButtonElement) || !target.classList.contains('dtp-time-option')) {
        return;
      }

      target.scrollIntoView({ block: 'nearest' });
    });

    const monthSwitcher = this.instanseElement.querySelector('.dtp-month-switcher');
    monthSwitcher?.addEventListener('click', event => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.classList.contains('dtp-icon-next')) {
        event.preventDefault();
        const components = getDateComponentsWithTimezone(this.showingDate, this.options.timezone);
        this.showingDate = createDateWithTimezone(
          components.year,
          components.month + 1,
          1,
          this.options.timezone,
        );
        this.changeDate();
      } else if (target.classList.contains('dtp-icon-previous')) {
        event.preventDefault();
        const components = getDateComponentsWithTimezone(this.showingDate, this.options.timezone);
        this.showingDate = createDateWithTimezone(
          components.year,
          components.month - 1,
          1,
          this.options.timezone,
        );
        this.changeDate();
      }
    });

    this.instanseElement
      .querySelector('.dtp-month-and-year-header .month')
      ?.addEventListener('click', event => {
        event.preventDefault();
        this.renderMonthPicker();
        this.setPickerView('month');
      });

    this.instanseElement
      .querySelector('.dtp-month-and-year-header .year')
      ?.addEventListener('click', event => {
        event.preventDefault();
        const showingComponents = getDateComponentsWithTimezone(
          this.showingDate,
          this.options.timezone,
        );
        this.yearPickerStart = showingComponents.year - 6;
        this.renderYearPicker();
        this.setPickerView('year');
      });

    this.instanseElement
      .querySelector('.dtp-month-picker-section')
      ?.addEventListener('click', event => {
        const target = event.target;
        if (!(target instanceof Element)) {
          return;
        }

        const monthButton = target.closest('[data-month]');
        if (!(monthButton instanceof HTMLButtonElement) || monthButton.disabled) {
          return;
        }

        const month = Number(monthButton.dataset.month);
        if (!Number.isInteger(month) || month < 0 || month > 11) {
          return;
        }

        const showingComponents = getDateComponentsWithTimezone(
          this.showingDate,
          this.options.timezone,
        );
        this.changeDate(showingComponents.year, month);

        if (this.mode === 'month') {
          const selectedDate = createDateWithTimezone(
            showingComponents.year,
            month,
            1,
            this.options.timezone,
          );
          if (!this.isDateValid(selectedDate)) return;
          this.selectedDate = [selectedDate];
          this.selectedTime = this.getTimeValue(selectedDate);
          this.updateSelectedDateLabel();
          const payload = this.getSelectedValuePayload();
          if (payload !== null) {
            this.emit('select', {
              date: payload,
              picker: this,
            });
          }
          if (this.shouldCommitSelectedDateOnSelect()) {
            this.commitSelectedDateToInput(true);
          }
        } else {
          this.setPickerView('calendar');
        }
      });

    this.instanseElement
      .querySelector('.dtp-year-picker-section')
      ?.addEventListener('click', event => {
        const target = event.target;
        if (!(target instanceof Element)) {
          return;
        }

        const navButton = target.closest('[data-year-nav]');
        if (navButton instanceof HTMLElement) {
          const step = Number(navButton.dataset.yearNav);
          if (Number.isInteger(step) && step !== 0) {
            this.yearPickerStart += step;
            this.renderYearPicker();
          }
          return;
        }

        const yearButton = target.closest('[data-year]');
        if (!(yearButton instanceof HTMLElement)) {
          return;
        }

        const year = Number(yearButton.dataset.year);
        if (!Number.isInteger(year)) {
          return;
        }

        const showingComponents = getDateComponentsWithTimezone(
          this.showingDate,
          this.options.timezone,
        );
        this.changeDate(year, showingComponents.month);
        this.setPickerView(this.mode === 'month' ? 'month' : 'calendar');
      });

    this.instanseElement
      .querySelector('.dtp-cancel-btn.dtp-btn')
      ?.addEventListener('click', event => {
        event.preventDefault();
        this.rangePreviewDate = null;
        this.selectedDate = this.appliedDate.map(date => new Date(date.getTime()));
        this.selectedTime = this.appliedTime;
        this.setShowingDate(this.selectedDate[0] ?? this.today);
        this.changeDate();
        this.syncTimeUi();
        this.updateRangeEndInputVisibility();
        this.setPickerOpen(false);
      });

    this.instanseElement.querySelector('.dtp-ok-btn.dtp-btn')?.addEventListener('click', event => {
      event.preventDefault();
      this.commitSelectedDateToInput(false);
    });

    // Add keyboard navigation
    this.addKeyboardNavigation();

    // Add window resize listener for adaptive positioning
    this.addResizeListener();

    // Add touch swipe listeners for mobile month navigation
    this.addTouchSwipeListeners();
  }

  protected previewRangeToCell(cell: HTMLTableCellElement, calendarBody: Element): void {
    if (this.options.workType !== WORK_TYPE.MULTI || this.selectedDate.length !== 1) {
      return;
    }

    if (!cell.classList.contains('current') || this.isDayCellDisabled(cell)) {
      this.clearRangePreview();
      return;
    }

    const dayCells = Array.from(calendarBody.querySelectorAll<HTMLTableCellElement>('td'));
    const cellIndex = dayCells.indexOf(cell);
    if (cellIndex < 0 || this.lastRenderedDays.length === 0) {
      return;
    }

    const previewDate = this.getDateForDayIndex(cellIndex, this.lastRenderedDays);
    if (!this.isDateValid(previewDate)) {
      this.clearRangePreview();
      return;
    }

    if (
      this.rangePreviewDate instanceof Date &&
      isSameDayWithTimezone(this.rangePreviewDate, previewDate, this.options.timezone)
    ) {
      return;
    }

    this.rangePreviewDate = previewDate;
    this.setCalendarDays(this.getAvailableMonth());
  }

  protected clearRangePreview(): void {
    if (!(this.rangePreviewDate instanceof Date)) {
      return;
    }

    this.rangePreviewDate = null;
    this.setCalendarDays(this.getAvailableMonth());
  }

  /**
   * Добавить обработчик изменения размера окна для адаптивного позиционирования
   * Также отслеживает изменение visualViewport (виртуальная клавиатура на мобильных)
   */
  protected addResizeListener(): void {
    // Используем debounce для оптимизации производительности
    let resizeTimeout: number | null = null;

    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      resizeTimeout = window.setTimeout(() => {
        if (this.isPickerOpen) {
          this.positionPicker();
        }
      }, 100);
    };

    window.addEventListener('resize', handleResize);

    // Сохраняем ссылку на обработчик для очистки
    this.resizeHandler = handleResize;
    this.cleanupHandlers.push(() => {
      if (resizeTimeout !== null) clearTimeout(resizeTimeout);
    });

    // Обработка виртуальной клавиатуры на мобильных устройствах
    // visualViewport API позволяет отслеживать изменение видимой области
    // при открытии/закрытии виртуальной клавиатуры
    if (window.visualViewport) {
      const visualViewport = window.visualViewport as VisualViewport;
      let keyboardTimeout: number | null = null;

      const handleVisualViewportChange = () => {
        if (!this.isPickerOpen || window.innerWidth >= 768) return;

        if (keyboardTimeout) {
          clearTimeout(keyboardTimeout);
        }

        keyboardTimeout = window.setTimeout(() => {
          const wrapper = this.getPickerWrapper();
          if (!(wrapper instanceof HTMLElement)) return;

          // Определяем, открыта ли виртуальная клавиатура
          // Если разница между window.innerHeight и visualViewport.height > 100px
          const keyboardHeight = window.innerHeight - visualViewport.height;

          if (keyboardHeight > 100) {
            // Виртуальная клавиатура открыта — поднимаем пикер
            const availableHeight = visualViewport.height - 16;
            wrapper.style.position = 'fixed';
            wrapper.style.top = `${Math.max(8, visualViewport.offsetTop)}px`;
            wrapper.style.maxHeight = `${Math.min(availableHeight, 400)}px`;
            wrapper.style.overflowY = 'auto';
            wrapper.style.zIndex = '9999';
          } else {
            // Виртуальная клавиатура закрыта — возвращаем стандартное позиционирование
            this.positionPicker();
          }
        }, 150);
      };

      visualViewport.addEventListener('resize', handleVisualViewportChange);
      visualViewport.addEventListener('scroll', handleVisualViewportChange);
      this.cleanupHandlers.push(() => {
        visualViewport.removeEventListener('resize', handleVisualViewportChange);
        visualViewport.removeEventListener('scroll', handleVisualViewportChange);
        if (keyboardTimeout !== null) clearTimeout(keyboardTimeout);
      });
    }
  }

  /**
   * Добавить обработку touch-свайпа для переключения месяцев на мобильных устройствах
   * Горизонтальный свайп по телу календаря переключает месяц
   */
  protected addTouchSwipeListeners(): void {
    const calendarBody = this.instanseElement.querySelector('.dtp-calender tbody');
    if (!calendarBody) return;

    let startX = 0;
    let startY = 0;
    let isSwiping = false;

    calendarBody.addEventListener(
      'touchstart',
      (e: Event) => {
        const touchEvent = e as TouchEvent;
        startX = touchEvent.touches[0].clientX;
        startY = touchEvent.touches[0].clientY;
        isSwiping = false;
      },
      { passive: true },
    );

    calendarBody.addEventListener(
      'touchmove',
      (e: Event) => {
        if (!this.isPickerOpen) return;

        const touchEvent = e as TouchEvent;
        const deltaX = touchEvent.touches[0].clientX - startX;
        const deltaY = touchEvent.touches[0].clientY - startY;

        // Определяем, что это горизонтальный свайп (по горизонтали больше, чем по вертикали)
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
          isSwiping = true;
          // Предотвращаем скролл страницы при горизонтальном свайпе
          e.preventDefault();
        }
      },
      { passive: false },
    );

    calendarBody.addEventListener(
      'touchend',
      (e: Event) => {
        if (!this.isPickerOpen || !isSwiping) return;

        const touchEvent = e as TouchEvent;
        const deltaX = touchEvent.changedTouches[0].clientX - startX;
        const threshold = 50;

        if (deltaX > threshold) {
          // Свайп вправо — предыдущий месяц
          const showingComponents = getDateComponentsWithTimezone(
            this.showingDate,
            this.options.timezone,
          );
          this.changeDate(showingComponents.year, showingComponents.month - 1);
        } else if (deltaX < -threshold) {
          // Свайп влево — следующий месяц
          const showingComponents = getDateComponentsWithTimezone(
            this.showingDate,
            this.options.timezone,
          );
          this.changeDate(showingComponents.year, showingComponents.month + 1);
        }

        isSwiping = false;
      },
      { passive: true },
    );
  }

  protected addManualInputListeners(): void {
    this.bindManualInput(this.displayInputElement, this.inputElement);
    this.bindManualInput(this.rangeEndDisplayInputElement, this.rangeEndInputElement);
  }

  protected bindManualInput(
    displayInput: HTMLInputElement | null,
    valueInput: HTMLInputElement | null,
  ): void {
    if (!(displayInput instanceof HTMLInputElement) || !(valueInput instanceof HTMLInputElement)) {
      return;
    }

    displayInput.addEventListener('input', () => {
      this.manuallyEditedInputs.add(displayInput);
      this.formatManualInput(displayInput);
      this.previewManualInput(displayInput, valueInput);
    });

    displayInput.addEventListener('blur', () => {
      this.applyManualInput(displayInput, valueInput);
    });

    displayInput.addEventListener('keydown', event => {
      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (this.applyManualInput(displayInput, valueInput)) {
        if (this.isPickerOpen) {
          this.setPickerOpen(false);
        }
      }
    });

    const form = displayInput.form;
    if (form instanceof HTMLFormElement) {
      const submitHandler: EventListener = event => {
        if (this.applyManualInput(displayInput, valueInput)) {
          return;
        }

        event.preventDefault();
        displayInput.reportValidity();
      };

      form.addEventListener('submit', submitHandler, { capture: true });
      this.manualSubmitHandlers.push({ form, handler: submitHandler });
    }
  }

  protected formatManualInput(displayInput: HTMLInputElement): void {
    if (displayInput.readOnly || displayInput.disabled) {
      return;
    }

    const value = displayInput.value;
    const selectionStart = displayInput.selectionStart ?? value.length;
    const digitCountBeforeCaret = value.slice(0, selectionStart).replace(/\D/g, '').length;
    const formattedValue = this.formatManualInputValue(value);

    if (formattedValue === value) {
      return;
    }

    displayInput.value = formattedValue;
    const nextCaretPosition = this.getCaretPositionByDigitCount(
      formattedValue,
      digitCountBeforeCaret,
    );
    displayInput.setSelectionRange(nextCaretPosition, nextCaretPosition);
  }

  protected formatManualInputValue(value: string): string {
    if (
      this.options.workType === WORK_TYPE.MULTI &&
      !(this.rangeEndInputElement instanceof HTMLInputElement)
    ) {
      const dateFormat = this.resolveDisplayFormat();
      const dateDigitCount = this.getFormatDigitCount(dateFormat);
      const parts = value.includes(this.options.rangeSeparator)
        ? this.splitRangeValue(value)
        : (value.replace(/\D/g, '').match(new RegExp(`.{1,${dateDigitCount}}`, 'g')) ?? []);

      return parts
        .slice(0, 2)
        .map(part => formatDigitsByPattern(part, dateFormat))
        .filter(part => part !== '')
        .join(this.options.rangeSeparator);
    }

    return formatDigitsByPattern(value, this.resolveDisplayFormat());
  }

  protected getFormatDigitCount(format: string): number {
    const tokenLengths: Record<string, number> = {
      yyyy: 4,
      MM: 2,
      dd: 2,
      HH: 2,
      mm: 2,
    };

    return (format.match(/yyyy|MM|dd|HH|mm/g) ?? []).reduce(
      (sum, token) => sum + tokenLengths[token],
      0,
    );
  }

  protected getCaretPositionByDigitCount(value: string, digitCount: number): number {
    if (digitCount <= 0) {
      return 0;
    }

    let seenDigits = 0;
    for (let index = 0; index < value.length; index += 1) {
      if (/\d/.test(value[index])) {
        seenDigits += 1;
      }

      if (seenDigits >= digitCount) {
        return index + 1;
      }
    }

    return value.length;
  }

  protected previewManualInput(
    displayInput: HTMLInputElement,
    valueInput: HTMLInputElement,
  ): boolean {
    if (displayInput.readOnly || displayInput.disabled) {
      return false;
    }

    const value = displayInput.value.trim();
    if (value === '') {
      this.clearDisplayInputValidity(displayInput);
      return true;
    }

    if (!this.isManualInputComplete(value)) {
      this.setDisplayInputPending(displayInput);
      return false;
    }

    return this.syncManualInput(displayInput, valueInput, false);
  }

  protected isManualInputComplete(value: string): boolean {
    if (
      this.options.workType === WORK_TYPE.MULTI &&
      !(this.rangeEndInputElement instanceof HTMLInputElement)
    ) {
      return this.splitRangeValue(value).every(
        part =>
          part.replace(/\D/g, '').length === this.getFormatDigitCount(this.resolveDisplayFormat()),
      );
    }

    return (
      value.replace(/\D/g, '').length === this.getFormatDigitCount(this.resolveDisplayFormat())
    );
  }

  protected applyManualInput(
    displayInput: HTMLInputElement,
    valueInput: HTMLInputElement,
  ): boolean {
    if (displayInput.readOnly || displayInput.disabled) {
      return false;
    }

    this.formatManualInput(displayInput);
    const value = displayInput.value.trim();
    if (value === '') {
      if (!this.manuallyEditedInputs.has(displayInput) && this.isPickerOpen) {
        this.clearDisplayInputValidity(displayInput);
        return true;
      }

      this.selectedDate = [];
      this.appliedDate = [];
      this.applySelectedDateToInput();
      this.changeDate();
      this.clearDisplayInputValidity(displayInput);
      this.manuallyEditedInputs.delete(displayInput);
      return true;
    }

    if (!this.isManualInputComplete(value)) {
      this.setDisplayInputInvalid(displayInput);
      return false;
    }

    const didSync = this.syncManualInput(displayInput, valueInput, true);
    if (didSync) {
      this.manuallyEditedInputs.delete(displayInput);
    }
    return didSync;
  }

  protected syncManualInput(
    displayInput: HTMLInputElement,
    valueInput: HTMLInputElement,
    shouldCommit: boolean,
  ): boolean {
    const value = displayInput.value.trim();
    const parsedDates =
      this.options.workType === WORK_TYPE.MULTI &&
      !(this.rangeEndInputElement instanceof HTMLInputElement)
        ? this.parseManualRangeValue(value)
        : this.parseManualSingleValue(value);

    if (parsedDates.length === 0 || parsedDates.some(date => !this.isDateValid(date))) {
      this.setDisplayInputInvalid(displayInput);
      return false;
    }

    const previousSelection = this.selectedDate;
    if (this.options.workType === WORK_TYPE.MULTI) {
      this.applyManualRangeDates(parsedDates, valueInput);
    } else {
      this.selectedDate = [parsedDates[0]];
    }

    if (!this.isSelectionValid(this.selectedDate)) {
      this.selectedDate = previousSelection;
      this.setDisplayInputInvalid(displayInput);
      return false;
    }
    const firstDate = this.selectedDate[0];
    if (firstDate instanceof Date) {
      this.selectedTime = this.getTimeValue(firstDate);
      this.setShowingDate(firstDate);
    }

    this.changeDate();
    this.syncTimeUi();
    this.updateRangeEndInputVisibility();
    if (shouldCommit) {
      this.appliedDate = this.selectedDate.map(date => new Date(date.getTime()));
      this.appliedTime = this.selectedTime;
      this.emitManualSelect();
      this.applySelectedDateToInput();
    }
    this.clearDisplayInputValidity(displayInput);
    return true;
  }

  protected parseManualSingleValue(value: string): Date[] {
    const parsed = this.parseInputValue(value, 'text');
    return parsed instanceof Date ? [parsed] : [];
  }

  protected parseManualRangeValue(value: string): Date[] {
    const parsed = this.splitRangeValue(value).map(part => this.parseInputValue(part, 'text'));
    if (parsed.length > 2 || parsed.some(item => item === null)) return [];
    return (parsed as Date[]).sort((a, b) => a.getTime() - b.getTime());
  }

  protected applyManualRangeDates(dates: Date[], valueInput: HTMLInputElement): void {
    if (this.rangeEndInputElement instanceof HTMLInputElement) {
      const existingDates = this.selectedDate.map(date => new Date(date.getTime()));
      if (valueInput === this.rangeEndInputElement) {
        this.selectedDate = [existingDates[0], dates[0]].filter(
          (item): item is Date => item instanceof Date,
        );
      } else {
        this.selectedDate = [dates[0], existingDates[1]].filter(
          (item): item is Date => item instanceof Date,
        );
      }

      this.selectedDate.sort((a, b) => a.getTime() - b.getTime());
      return;
    }

    this.selectedDate = dates;
  }

  protected emitManualSelect(): void {
    const selectedValue = this.getSelectedValuePayload();
    if (selectedValue === null) {
      return;
    }

    this.emit('select', {
      date: selectedValue,
      picker: this,
    });
  }

  /**
   * Добавить обработчики клавиатуры для навигации
   */
  protected addKeyboardNavigation(): void {
    if (this.keyboardNavigationHandler) {
      return;
    }

    // Add global keydown listener to capture events when picker is open
    // This prevents browser scroll and other default behaviors
    // Используем один глобальный обработчик вместо нескольких, чтобы избежать дублирования событий
    this.keyboardNavigationHandler = (event: KeyboardEvent) => {
      if (!this.isPickerOpen) {
        return;
      }

      // Обрабатываем только клавиши навигации
      if (!this.shouldHandleKey(event.key)) {
        return;
      }

      if (this.shouldLetFocusedControlHandleKey(event)) {
        return;
      }

      // Предотвращаем поведение браузера (прокрутку и т.д.)
      event.preventDefault();
      event.stopPropagation();

      this.handleKeyDown(event);
    };

    document.addEventListener('keydown', this.keyboardNavigationHandler);
  }

  protected shouldHandleKey(key: string): boolean {
    const navigationKeys = [
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'PageUp',
      'PageDown',
      'Home',
      'End',
      'Tab', // Добавляем Tab для focus trap
    ];
    return navigationKeys.includes(key);
  }

  protected shouldLetFocusedControlHandleKey(event: KeyboardEvent): boolean {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return false;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    const wrapper = this.getPickerWrapper();
    if (!wrapper?.contains(target)) {
      return false;
    }

    return target.matches('button, a[href], input, select, textarea, [role="button"]');
  }

  /**
   * Обработка нажатий клавиш
   */
  protected handleKeyDown(event: KeyboardEvent): void {
    if (!this.isPickerOpen) {
      return;
    }

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.setPickerOpen(false);
        break;

      case 'Enter':
        if (this.pickerView === 'calendar' && this.focusedDayIndex >= 0) {
          event.preventDefault();
          this.selectFocusedDay();
        }
        break;

      case 'ArrowLeft':
        event.preventDefault();
        this.navigateLeft();
        break;

      case 'ArrowRight':
        event.preventDefault();
        this.navigateRight();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.navigateUp();
        break;

      case 'ArrowDown':
        event.preventDefault();
        this.navigateDown();
        break;

      case 'PageUp':
        event.preventDefault();
        this.navigatePageUp(event);
        break;

      case 'PageDown':
        event.preventDefault();
        this.navigatePageDown(event);
        break;

      case 'Tab':
        // Управление Tab навигацией внутри пикера
        event.preventDefault();
        this.handleTabNavigation(event);
        break;

      case 'Home':
        event.preventDefault();
        this.navigateToStart();
        break;

      case 'End':
        event.preventDefault();
        this.navigateToEnd();
        break;
    }
  }

  /**
   * Выбрать сфокусированный день
   */
  protected selectFocusedDay(): void {
    if (this.focusedDayIndex < 0) {
      return;
    }

    const calendarBody = this.instanseElement.querySelector('.dtp-wrapper .dtp-calender tbody');
    if (!calendarBody) {
      return;
    }

    const dayCells = calendarBody.querySelectorAll<HTMLTableCellElement>('td');
    if (this.focusedDayIndex >= dayCells.length) {
      return;
    }

    const cell = dayCells[this.focusedDayIndex];
    // Если день disabled, не выбирать
    if (this.isDayCellDisabled(cell)) {
      return;
    }

    // Используем lastRenderedDays для вычисления даты
    if (this.lastRenderedDays.length === 0) {
      return;
    }
    const selectedDate = this.getDateForDayIndex(this.focusedDayIndex, this.lastRenderedDays);

    // Проверить валидность даты
    if (!this.isDateValid(selectedDate)) {
      return; // Не выбирать невалидную дату
    }

    if (this.options.workType === WORK_TYPE.SINGLE) {
      this.selectedDate = [selectedDate];
      this.emit('select', {
        date: selectedDate,
        picker: this,
      });
      this.changeDate();
      if (this.shouldShowDatetimeTimeStepOnSelect()) {
        this.setDatetimeStep('time', { openTimeGrid: true });
      } else if (this.shouldCommitSelectedDateOnSelect()) {
        this.commitSelectedDateToInput(true);
      }
    } else if (this.options.workType === WORK_TYPE.MULTI) {
      if (
        this.selectedDate.length === 1 &&
        !this.isSelectionValid(
          [this.selectedDate[0], selectedDate].sort((a, b) => a.getTime() - b.getTime()),
        )
      )
        return;
      // Для multi режима - добавить дату в диапазон
      if (this.selectedDate.length === 0 || this.selectedDate.length >= 2) {
        this.selectedDate = [selectedDate];
        this.rangePreviewDate = null;
      } else {
        const start = this.selectedDate[0];
        if (selectedDate.getTime() < start.getTime()) {
          this.selectedDate = [selectedDate, start];
        } else {
          this.selectedDate = [start, selectedDate];
        }
        this.rangePreviewDate = null;
      }
      this.emit('select', {
        date: this.selectedDate,
        picker: this,
      });
      this.changeDate();
      if (this.shouldCommitSelectedDateOnSelect()) {
        this.commitSelectedDateToInput(true);
      }
    }
  }

  /**
   * Навигация влево
   */
  protected navigateLeft(): void {
    if (this.pickerView === 'calendar') {
      const calendarBody = this.instanseElement.querySelector('.dtp-wrapper .dtp-calender tbody');
      if (!calendarBody) {
        return;
      }

      const dayCells = calendarBody.querySelectorAll<HTMLTableCellElement>('td');
      if (dayCells.length === 0) {
        return;
      }

      const newIndex = this.findNextValidDayIndex(this.focusedDayIndex, -1, dayCells);
      if (newIndex >= 0) {
        this.focusedDayIndex = newIndex;
      } else {
        // Если не нашли валидный день влево, остаемся на текущем (если он валиден)
        if (this.focusedDayIndex >= 0 && this.focusedDayIndex < dayCells.length) {
          if (this.isDayCellDisabled(dayCells[this.focusedDayIndex])) {
            // Текущий день disabled, сбросить фокус
            this.focusedDayIndex = -1;
          }
        }
      }
      this.updateDayFocus();
    }
  }

  /**
   * Навигация вправо
   */
  protected navigateRight(): void {
    if (this.pickerView === 'calendar') {
      const calendarBody = this.instanseElement.querySelector('.dtp-wrapper .dtp-calender tbody');
      if (!calendarBody) {
        return;
      }

      const dayCells = calendarBody.querySelectorAll<HTMLTableCellElement>('td');
      if (dayCells.length === 0) {
        return;
      }

      // Просто увеличиваем индекс на 1
      let newIndex = this.focusedDayIndex + 1;

      // Если вышли за границы, остаемся на последнем
      if (newIndex >= dayCells.length) {
        newIndex = dayCells.length - 1;
      }

      // Если новый день disabled, найти ближайший валидный
      if (this.isDayCellDisabled(dayCells[newIndex])) {
        const validIndex = this.findNextValidDayIndex(newIndex, 1, dayCells);
        if (validIndex >= 0) {
          newIndex = validIndex;
        } else {
          // Не нашли валидный день вправо, попробовать влево
          const validIndexLeft = this.findNextValidDayIndex(newIndex, -1, dayCells);
          if (validIndexLeft >= 0) {
            newIndex = validIndexLeft;
          } else {
            // Нет валидных дней, остаться на текущем
            return;
          }
        }
      }

      this.focusedDayIndex = newIndex;
      this.updateDayFocus();
    }
  }

  /**
   * Навигация вверх
   */
  protected navigateUp(): void {
    if (this.pickerView === 'calendar') {
      const calendarBody = this.instanseElement.querySelector('.dtp-wrapper .dtp-calender tbody');
      if (!calendarBody) {
        return;
      }

      const dayCells = calendarBody.querySelectorAll<HTMLTableCellElement>('td');
      this.focusedDayIndex = Math.max(0, this.focusedDayIndex - 7);
      // Убедиться, что индекс не превышает количество ячеек
      if (this.focusedDayIndex >= dayCells.length) {
        this.focusedDayIndex = dayCells.length - 1;
      }
      this.updateDayFocus();
    }
  }

  /**
   * Навигация вниз
   */
  protected navigateDown(): void {
    if (this.pickerView === 'calendar') {
      const calendarBody = this.instanseElement.querySelector('.dtp-wrapper .dtp-calender tbody');
      if (!calendarBody) {
        return;
      }

      const dayCells = calendarBody.querySelectorAll<HTMLTableCellElement>('td');
      this.focusedDayIndex = Math.min(dayCells.length - 1, this.focusedDayIndex + 7);
      this.updateDayFocus();
    }
  }

  /**
   * Навигация PageUp (предыдущий месяц)
   */
  protected navigatePageUp(event: KeyboardEvent): void {
    event.preventDefault();
    if (this.pickerView === 'calendar') {
      const components = getDateComponentsWithTimezone(this.showingDate, this.options.timezone);
      this.showingDate = createDateWithTimezone(
        components.year,
        components.month - 1,
        1,
        this.options.timezone,
      );
      this.changeDate();
      this.resetFocus();
    }
  }

  /**
   * Навигация PageDown (следующий месяц)
   */
  protected navigatePageDown(event: KeyboardEvent): void {
    event.preventDefault();
    if (this.pickerView === 'calendar') {
      const components = getDateComponentsWithTimezone(this.showingDate, this.options.timezone);
      this.showingDate = createDateWithTimezone(
        components.year,
        components.month + 1,
        1,
        this.options.timezone,
      );
      this.changeDate();
      this.resetFocus();
    }
  }

  /**
   * Обработка Tab навигации - ловушка фокуса внутри пикера
   */
  protected handleTabNavigation(event: KeyboardEvent): void {
    if (!this.isPickerOpen) {
      return;
    }

    const wrapper = this.getPickerWrapper();
    if (!wrapper) {
      return;
    }

    // Получить все фокусируемые элементы внутри пикера
    const focusableSelectors = [
      'button:not([disabled])',
      '[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
      '.dtp-calender td[tabindex="0"]',
    ].join(', ');

    const focusableElements = Array.from(
      wrapper.querySelectorAll<HTMLElement>(focusableSelectors),
    ).filter(el => el.offsetParent !== null); // Только видимые элементы

    if (focusableElements.length === 0) {
      return;
    }

    const currentElement = document.activeElement as HTMLElement;
    const currentIndex = focusableElements.indexOf(currentElement);

    // Если текущий элемент не в списке или это первый/последний элемент с Shift+Tab
    if (currentIndex === -1) {
      // Если нажата Shift+Tab, перейти к последнему элементу
      if (event.shiftKey) {
        event.preventDefault();
        focusableElements[focusableElements.length - 1]?.focus();
      } else {
        // Иначе перейти к первому элементу
        event.preventDefault();
        focusableElements[0]?.focus();
      }
      return;
    }

    // Определить следующий индекс
    let nextIndex;
    if (event.shiftKey) {
      // Shift+Tab: двигаться назад
      nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
    } else {
      // Tab: двигаться вперёд
      nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
    }

    event.preventDefault();
    focusableElements[nextIndex]?.focus();
  }

  /**
   * Навигация к началу (первый день месяца)
   */
  protected navigateToStart(): void {
    if (this.pickerView === 'calendar') {
      this.focusedDayIndex = 0;
      this.updateDayFocus();
    }
  }

  /**
   * Навигация к концу (последний день месяца)
   */
  protected navigateToEnd(): void {
    if (this.pickerView === 'calendar') {
      const calendarBody = this.instanseElement.querySelector('.dtp-wrapper .dtp-calender tbody');
      if (!calendarBody) {
        return;
      }

      const dayCells = calendarBody.querySelectorAll<HTMLTableCellElement>('td');
      this.focusedDayIndex = dayCells.length - 1;
      this.updateDayFocus();
    }
  }

  /**
   * Проверяет, отключена ли ячейка дня (имеет класс disabled)
   */
  protected isDayCellDisabled(cell: HTMLTableCellElement): boolean {
    return cell.classList.contains('disabled');
  }

  /**
   * Находит индекс следующего валидного дня в заданном направлении
   * @param startIndex Начальный индекс
   * @param direction Направление (1 для вперед, -1 для назад)
   * @param dayCells Коллекция ячеек дней
   * @returns Индекс валидного дня или -1 если не найден
   */
  protected findNextValidDayIndex(
    startIndex: number,
    direction: number,
    dayCells: NodeListOf<HTMLTableCellElement>,
  ): number {
    let index = startIndex + direction;
    while (index >= 0 && index < dayCells.length) {
      if (!this.isDayCellDisabled(dayCells[index])) {
        return index;
      }
      index += direction;
    }
    return -1; // не найден
  }

  /**
   * Обновить визуальное выделение сфокусированного дня
   */
  protected updateDayFocus(): void {
    const calendarBody = this.instanseElement.querySelector('.dtp-wrapper .dtp-calender tbody');
    if (!calendarBody) {
      return;
    }

    // Убрать выделение со всех дней
    const allCells = calendarBody.querySelectorAll<HTMLTableCellElement>('td');
    allCells.forEach(cell => {
      cell.classList.remove('dtp-focused');
      cell.removeAttribute('tabindex');
    });

    // Добавить выделение на сфокусированный день
    const dayCells = allCells; // Используем все ячейки, а не только .current
    if (this.focusedDayIndex >= 0 && this.focusedDayIndex < dayCells.length) {
      const focusedCell = dayCells[this.focusedDayIndex];
      // Если день disabled, найти ближайший валидный день
      if (this.isDayCellDisabled(focusedCell)) {
        // Попробовать найти валидный день в обе стороны
        const nextValidIndex = this.findNextValidDayIndex(this.focusedDayIndex, 1, dayCells);
        if (nextValidIndex >= 0) {
          this.focusedDayIndex = nextValidIndex;
        } else {
          const prevValidIndex = this.findNextValidDayIndex(this.focusedDayIndex, -1, dayCells);
          if (prevValidIndex >= 0) {
            this.focusedDayIndex = prevValidIndex;
          } else {
            // Нет валидных дней, сбросить фокус
            this.focusedDayIndex = -1;
            return;
          }
        }
      }

      const validCell = dayCells[this.focusedDayIndex];
      validCell.classList.add('dtp-focused');
      validCell.setAttribute('tabindex', '0');
      // Не вызываем focus() здесь, чтобы не мешать навигации стрелками
      // Фокус будет установлен только при явном переходе по Tab
    }
  }

  /**
   * Сбросить фокус
   */
  protected resetFocus(): void {
    this.focusedDayIndex = -1;
    this.focusedMonthIndex = -1;
    this.focusedYearIndex = -1;
  }

  protected resolveDisplayFormat(): string {
    if (this.mode === 'time') {
      return this.options.displayFormat ?? 'HH:mm';
    }

    if (this.mode === 'month') {
      return this.options.displayFormat ?? 'MM.yyyy';
    }

    const dateFormat =
      this.options.displayFormatsByLang[this.options.lang] ??
      this.options.displayFormatsByLang[this.options.fallbackLang] ??
      'dd.MM.yyyy';

    if (DateTimePicker.DATETIME_MODE_ALIASES.has(this.mode)) {
      return this.options.displayFormat ?? `${dateFormat} HH:mm`;
    }

    return this.options.displayFormat ?? dateFormat;
  }

  protected resolveValueFormat(inputType?: string): string {
    if (typeof this.options.valueFormat === 'string' && this.options.valueFormat.trim() !== '') {
      return this.options.valueFormat;
    }

    if (inputType === 'time' || this.mode === 'time') {
      return 'HH:mm';
    }

    if (inputType === 'datetime-local' || DateTimePicker.DATETIME_MODE_ALIASES.has(this.mode)) {
      return 'yyyy-MM-ddTHH:mm';
    }

    if (inputType === 'month' || this.mode === 'month') {
      return 'yyyy-MM';
    }

    return 'yyyy-MM-dd';
  }

  protected resolveMode(): PickerMode {
    return this.resolveModeForInput(this.inputElement);
  }

  protected resolveModeForInput(input: HTMLInputElement | null): PickerMode {
    if (typeof this.options.mode !== 'undefined') {
      return this.options.mode;
    }

    const inputType =
      input instanceof HTMLInputElement ? this.getInputTypeForValidation(input) : undefined;

    if (inputType === 'time') {
      return 'time';
    }
    if (inputType === 'datetime-local') {
      return 'datetime';
    }
    if (inputType === 'month') {
      return 'month';
    }

    return 'date';
  }

  protected assertModeMatchesInputType(): void {
    if (!(this.inputElement instanceof HTMLInputElement)) {
      return;
    }

    if (typeof this.options.mode === 'undefined') {
      return;
    }

    const inputType = this.getInputTypeForValidation(this.inputElement);
    const allowedTypesByMode: Record<PickerMode, string[]> = {
      date: ['date', 'month', 'text'],
      time: ['time', 'text'],
      datetime: ['datetime-local', 'text'],
      'datetime-local': ['datetime-local', 'text'],
      month: ['month', 'text'],
    };

    const allowedTypes = allowedTypesByMode[this.mode];
    if (allowedTypes.includes(inputType)) {
      return;
    }

    throw new Error(
      `Несовместимые настройки DateTimePicker: mode="${this.mode}" не соответствует input type="${inputType}". ` +
        `Для mode="${this.mode}" допустимы: ${allowedTypes.join(', ')}.`,
    );
  }

  protected getManagedInputType(input: HTMLInputElement): string {
    return resolveManagedInputType(this.getInputTypeForValidation(input), this.mode);
  }

  protected getInputTypeForValidation(input: HTMLInputElement): string {
    return input.dataset.dtpOriginalType || input.type;
  }

  protected normalizeModeOption(mode: PickerMode | undefined): PickerMode | undefined {
    if (mode === 'datetime-local') {
      return 'datetime';
    }

    return mode;
  }

  protected resolveRangeEndInput(): HTMLInputElement | null {
    if (this.options.workType !== WORK_TYPE.MULTI) {
      return null;
    }

    const selector = this.options.rangeEndSelector;
    if (typeof selector !== 'string' || selector.trim() === '') {
      return null;
    }

    const root = this.inputElement?.closest('form') ?? document;
    const found = root.querySelector(selector);
    if (found instanceof HTMLInputElement) {
      this.rangeEndDisplayInputElement = this.markManagedInput(found);
      return found;
    }

    return null;
  }

  protected markManagedInput(input: HTMLInputElement): HTMLInputElement {
    if (!this.inputSnapshots.has(input)) {
      this.inputSnapshots.set(
        input,
        new Map(
          [
            'type',
            'id',
            'hidden',
            'tabindex',
            'aria-hidden',
            'class',
            'data-dtp-original-type',
            'data-dtp-original-id',
          ].map(name => [name, input.getAttribute(name)]),
        ),
      );
    }
    input.dataset.dtpOriginalType = input.dataset.dtpOriginalType || input.type;

    const displayInput = this.resolveDisplayInput(input);
    this.syncDisplayInputAttributes(input, displayInput);

    input.type = 'hidden';
    input.hidden = true;
    input.tabIndex = -1;
    input.setAttribute('aria-hidden', 'true');
    input.classList.remove(DateTimePicker.MANAGED_INPUT_CLASS, DateTimePicker.DISPLAY_INPUT_CLASS);
    input.classList.add(DateTimePicker.VALUE_INPUT_CLASS);

    // Добавляем кнопку-иконку для открытия пикера
    this.addCalendarIcon(input, displayInput);

    return displayInput;
  }

  protected addCalendarIcon(valueInput: HTMLInputElement, displayInput: HTMLInputElement): void {
    // Удаляем старую иконку, если была
    const existingIcon = this.inputIcons.get(valueInput);
    if (existingIcon) {
      existingIcon.remove();
    }

    const icon = document.createElement('button');
    this.inputIcons.set(valueInput, icon);
    icon.type = 'button';
    icon.disabled = valueInput.disabled;
    icon.className = 'dtp-calendar-icon';
    const isTimeMode = this.resolveModeForInput(valueInput) === 'time';
    icon.classList.toggle('dtp-clock-icon', isTimeMode);
    icon.setAttribute('aria-label', isTimeMode ? this.t('select time') : 'Open calendar');
    icon.setAttribute('aria-haspopup', 'dialog');
    icon.setAttribute('aria-expanded', this.isPickerOpen.toString());
    icon.tabIndex = -1;
    icon.innerHTML = isTimeMode
      ? `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="9"></circle>
        <path d="M12 7v5l3 2"></path>
      </svg>`
      : `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
      </svg>`;

    // Вставляем иконку после displayInput
    displayInput.parentNode?.insertBefore(icon, displayInput.nextSibling);

    icon.addEventListener('pointerdown', event => {
      event.preventDefault();
    });

    // Клик по иконке открывает пикер
    icon.addEventListener('click', (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      this.ensureTimeModeSelectedDate();
      this.setPickerOpen(true);
      if (this.mode === 'time') {
        this.setTimeGridOpen(true);
        this.focusActiveTimeOption();
        return;
      }
      this.focusPickerAfterIconOpen();
    });
  }

  protected syncCalendarIconExpandedState(isOpen: boolean): void {
    const icons = this.instanseElement.querySelectorAll<HTMLButtonElement>('.dtp-calendar-icon');
    icons.forEach(icon => {
      icon.setAttribute('aria-expanded', isOpen.toString());
    });
  }

  protected focusPickerAfterIconOpen(): void {
    if (this.mode === 'time') {
      return;
    }

    const wrapper = this.getPickerWrapper();
    if (!(wrapper instanceof HTMLElement)) {
      return;
    }

    wrapper.tabIndex = -1;
    wrapper.focus({ preventScroll: true });
  }

  protected resolveDisplayInput(input: HTMLInputElement): HTMLInputElement {
    const nextElement = input.nextElementSibling;
    if (
      nextElement instanceof HTMLInputElement &&
      nextElement.classList.contains(DateTimePicker.DISPLAY_INPUT_CLASS)
    ) {
      return nextElement;
    }

    const displayInput = document.createElement('input');
    input.parentNode?.insertBefore(displayInput, input.nextSibling);
    return displayInput;
  }

  protected syncDisplayInputAttributes(
    valueInput: HTMLInputElement,
    displayInput: HTMLInputElement,
  ): void {
    const userClasses = Array.from(valueInput.classList).filter(
      className =>
        className !== DateTimePicker.MANAGED_INPUT_CLASS &&
        className !== DateTimePicker.DISPLAY_INPUT_CLASS &&
        className !== DateTimePicker.VALUE_INPUT_CLASS,
    );

    displayInput.className = userClasses.join(' ');
    displayInput.classList.add(
      DateTimePicker.MANAGED_INPUT_CLASS,
      DateTimePicker.DISPLAY_INPUT_CLASS,
    );
    displayInput.type = 'text';
    displayInput.value = valueInput.value;
    displayInput.placeholder = valueInput.placeholder;
    displayInput.disabled = valueInput.disabled;
    displayInput.required = valueInput.required;
    displayInput.readOnly = valueInput.readOnly || !this.options.manualInput;
    displayInput.autocomplete = 'off';
    displayInput.removeAttribute('name');
    this.syncDisplayInputKeyboardAttributes(displayInput);

    if (valueInput.title !== '') {
      displayInput.title = valueInput.title;
    } else {
      displayInput.removeAttribute('title');
    }

    const visibleId = valueInput.dataset.dtpOriginalId || valueInput.id;
    if (visibleId !== '') {
      valueInput.dataset.dtpOriginalId = visibleId;
      displayInput.id = visibleId;
      if (valueInput.id === visibleId) {
        valueInput.id = `${visibleId}-dtp-value`;
      }
    } else {
      displayInput.removeAttribute('id');
    }

    const ariaLabel = valueInput.getAttribute('aria-label');
    if (ariaLabel !== null) {
      displayInput.setAttribute('aria-label', ariaLabel);
    } else {
      displayInput.removeAttribute('aria-label');
    }

    displayInput.setAttribute('aria-haspopup', 'dialog');
    displayInput.setAttribute('aria-expanded', 'false');
    displayInput.setAttribute('aria-controls', this.dialogId);
  }

  protected syncDisplayInputKeyboardAttributes(displayInput: HTMLInputElement): void {
    displayInput.inputMode = 'numeric';
    displayInput.removeAttribute('pattern');
  }

  protected syncDisplayInputPlaceholders(): void {
    this.syncDisplayInputPlaceholder(this.inputElement, this.displayInputElement);
    this.syncDisplayInputPlaceholder(this.rangeEndInputElement, this.rangeEndDisplayInputElement);
  }

  protected syncDisplayInputPlaceholder(
    valueInput: HTMLInputElement | null,
    displayInput: HTMLInputElement | null,
  ): void {
    if (!(valueInput instanceof HTMLInputElement) || !(displayInput instanceof HTMLInputElement)) {
      return;
    }

    if (valueInput.placeholder === '') {
      displayInput.placeholder = this.resolveDisplayFormat();
    }
  }

  protected getTimeValue(date: Date): string {
    const components = getDateComponentsWithTimezone(date, this.options.timezone);
    return `${this.pad2(components.hours)}:${this.pad2(components.minutes)}`;
  }

  protected ensureTimeModeSelectedDate(): void {
    if (this.mode !== 'time' || this.selectedDate.length > 0) {
      return;
    }

    this.selectedDate = [this.today];
  }

  protected getSelectedDateTime(): Date | null {
    const baseDate = this.selectedDate[0];
    if (!(baseDate instanceof Date)) {
      return null;
    }

    return this.getDateWithSelectedTime(baseDate);
  }

  protected getDateWithSelectedTime(baseDate: Date): Date {
    const match = this.selectedTime.match(/^(\d{2}):(\d{2})$/);
    if (!match) {
      return new Date(baseDate.getTime());
    }

    const components = getDateComponentsWithTimezone(baseDate, this.options.timezone);
    return createDateTimeWithTimezone(
      components.year,
      components.month,
      components.day,
      Number(match[1]),
      Number(match[2]),
      this.options.timezone,
    );
  }

  protected getRangeStartEnd(): [Date | null, Date | null] {
    if (this.selectedDate.length === 0) {
      return [null, null];
    }

    if (this.selectedDate.length === 1) {
      return [this.selectedDate[0], null];
    }

    const sorted = [...this.selectedDate].sort((a, b) => a.getTime() - b.getTime());
    return [sorted[0], sorted[1]];
  }

  protected getSelectedValuePayload(): Date | Date[] | null {
    if (this.options.workType !== WORK_TYPE.MULTI) {
      return this.getSelectedDateTime();
    }

    const [start, end] = this.getRangeStartEnd();
    if (!(start instanceof Date)) {
      return null;
    }

    if (!(end instanceof Date)) {
      return [this.getDateWithSelectedTime(start)];
    }

    return [this.getDateWithSelectedTime(start), this.getDateWithSelectedTime(end)];
  }

  protected syncTimeUi(): void {
    const timeLabel = this.instanseElement.querySelector('.dtp-time');
    if (timeLabel instanceof HTMLElement) {
      timeLabel.innerText = this.selectedTime;
    }

    const timeManualInput = this.instanseElement.querySelector('.dtp-time-manual-input');
    if (timeManualInput instanceof HTMLInputElement) {
      timeManualInput.value = this.selectedTime;
      timeManualInput.removeAttribute('aria-invalid');
    }

    const [hour, minute] = this.selectedTime.split(':');
    this.syncTimeColumn('hour', hour);
    this.syncTimeColumn('minute', minute);
  }

  protected setTimeGridOpen(isOpen: boolean): void {
    const timeGrid = this.instanseElement.querySelector('.dtp-time-grid');
    if (timeGrid instanceof HTMLElement) {
      timeGrid.hidden = !isOpen;
    }
  }

  protected syncTimeColumn(part: 'hour' | 'minute', value: string): void {
    const column = this.instanseElement.querySelector(
      `.dtp-time-column[data-time-column="${part}"]`,
    );
    if (!(column instanceof HTMLElement)) {
      return;
    }

    column.querySelectorAll<HTMLButtonElement>('.dtp-time-option').forEach(option => {
      const isActive = option.dataset.timeValue === value;
      option.classList.toggle('active', isActive);
      option.setAttribute('aria-selected', isActive.toString());
      option.tabIndex = isActive ? 0 : -1;
    });
  }

  protected focusActiveTimeOption(): void {
    window.setTimeout(() => {
      const activeOption = this.instanseElement.querySelector<HTMLButtonElement>(
        '.dtp-time-column .dtp-time-option.active',
      );
      if (activeOption instanceof HTMLButtonElement) {
        activeOption.focus();
      }
    }, 0);
  }

  protected focusTimeManualInput(): void {
    window.setTimeout(() => {
      const timeManualInput =
        this.instanseElement.querySelector<HTMLInputElement>('.dtp-time-manual-input');
      if (timeManualInput instanceof HTMLInputElement) {
        timeManualInput.focus();
        timeManualInput.select();
      }
    }, 0);
  }

  protected selectTimeOption(option: HTMLButtonElement): void {
    const part = option.dataset.timePart;
    const value = option.dataset.timeValue;
    if ((part !== 'hour' && part !== 'minute') || typeof value !== 'string') {
      return;
    }

    const [currentHour = '00', currentMinute = '00'] = this.selectedTime.split(':');
    const nextTime = part === 'hour' ? `${value}:${currentMinute}` : `${currentHour}:${value}`;
    const previousTime = this.selectedTime;
    this.selectedTime = nextTime;
    this.ensureTimeModeSelectedDate();

    if (this.selectedTime !== previousTime) {
      this.markTimeChangedByUserDuringDateStep();
      this.emit('timeChange', {
        time: this.selectedTime,
        picker: this,
      });
    }

    this.syncTimeUi();
    this.updateSelectedDateLabel();
  }

  protected formatTimeManualInput(input: HTMLInputElement): void {
    const value = input.value;
    const selectionStart = input.selectionStart ?? value.length;
    const digitCountBeforeCaret = value.slice(0, selectionStart).replace(/\D/g, '').length;
    const formattedValue = formatDigitsByPattern(value, 'HH:mm');

    if (formattedValue === value) {
      return;
    }

    input.value = formattedValue;
    const nextCaretPosition = this.getCaretPositionByDigitCount(
      formattedValue,
      digitCountBeforeCaret,
    );
    input.setSelectionRange(nextCaretPosition, nextCaretPosition);
  }

  protected previewTimeManualInput(input: HTMLInputElement): boolean {
    const value = input.value.trim();
    if (value === '') {
      input.removeAttribute('aria-invalid');
      return true;
    }

    if (value.replace(/\D/g, '').length < 4) {
      input.removeAttribute('aria-invalid');
      return false;
    }

    return this.syncTimeManualInput(input);
  }

  protected applyTimeManualInput(input: HTMLInputElement): boolean {
    this.formatTimeManualInput(input);
    const value = input.value.trim();
    if (value === '') {
      input.value = this.selectedTime;
      input.removeAttribute('aria-invalid');
      return true;
    }

    if (value.replace(/\D/g, '').length < 4) {
      input.setAttribute('aria-invalid', 'true');
      return false;
    }

    return this.syncTimeManualInput(input);
  }

  protected syncTimeManualInput(input: HTMLInputElement): boolean {
    const parsed = this.parseInputValue(input.value, 'time');
    if (!(parsed instanceof Date)) {
      input.setAttribute('aria-invalid', 'true');
      return false;
    }

    const previousTime = this.selectedTime;
    this.selectedTime = this.getTimeValue(parsed);
    this.ensureTimeModeSelectedDate();

    if (this.selectedTime !== previousTime) {
      this.markTimeChangedByUserDuringDateStep();
      this.emit('timeChange', {
        time: this.selectedTime,
        picker: this,
      });
    }

    input.removeAttribute('aria-invalid');
    this.syncTimeUi();
    this.updateSelectedDateLabel();
    return true;
  }

  protected markTimeChangedByUserDuringDateStep(): void {
    if (DateTimePicker.DATETIME_MODE_ALIASES.has(this.mode) && this.datetimeStep === 'date') {
      this.timeChangedByUserDuringDateStep = true;
    }
  }

  protected applyModeVisibility(): void {
    this.syncPickerSections();
  }

  protected syncPickerSections(): void {
    const calendarSection = this.instanseElement.querySelector('.dtp-calender-section');
    const monthYearHeader = this.instanseElement.querySelector('.dtp-month-and-year-header');
    const monthPickerSection = this.instanseElement.querySelector('.dtp-month-picker-section');
    const yearPickerSection = this.instanseElement.querySelector('.dtp-year-picker-section');
    const timeSection = this.instanseElement.querySelector('.dtp-time-section');
    const timeDisplay = this.instanseElement.querySelector('.dtp-time');
    const selectPane = this.instanseElement.querySelector(
      '.dtp-select-pane:not(.dtp-month-switcher)',
    );
    const isDatetimeTimeStep =
      DateTimePicker.DATETIME_MODE_ALIASES.has(this.mode) && this.datetimeStep === 'time';
    const needHideTime =
      this.mode === 'date' ||
      this.mode === 'month' ||
      this.options.workType === WORK_TYPE.MULTI ||
      (!isDatetimeTimeStep && this.pickerView !== 'calendar');

    if (calendarSection instanceof HTMLElement) {
      calendarSection.hidden =
        isDatetimeTimeStep || this.pickerView !== 'calendar' || this.mode === 'time';
    }
    if (monthYearHeader instanceof HTMLElement) {
      monthYearHeader.hidden = isDatetimeTimeStep || this.mode === 'time';
    }
    if (monthPickerSection instanceof HTMLElement) {
      monthPickerSection.hidden =
        isDatetimeTimeStep || this.pickerView !== 'month' || this.mode === 'time';
    }
    if (yearPickerSection instanceof HTMLElement) {
      yearPickerSection.hidden =
        isDatetimeTimeStep || this.pickerView !== 'year' || this.mode === 'time';
    }
    if (timeSection instanceof HTMLElement) {
      timeSection.hidden = needHideTime;
    }
    if (timeDisplay instanceof HTMLElement) {
      timeDisplay.hidden = needHideTime;
    }
    if (selectPane instanceof HTMLElement) {
      selectPane.hidden = needHideTime;
    }
    this.syncUiStateClasses();
  }

  protected syncUiStateClasses(): void {
    const wrapper = this.getPickerWrapper();
    if (!(wrapper instanceof HTMLElement)) {
      return;
    }

    const isDatetimeTimeStep =
      DateTimePicker.DATETIME_MODE_ALIASES.has(this.mode) && this.datetimeStep === 'time';
    const hideTime =
      this.mode === 'date' ||
      this.mode === 'month' ||
      this.options.workType === WORK_TYPE.MULTI ||
      (!isDatetimeTimeStep && this.pickerView !== 'calendar');
    wrapper.classList.toggle('dtp-no-time', hideTime);
    wrapper.classList.toggle('dtp-datetime-time-step', isDatetimeTimeStep);
    wrapper.classList.toggle('dtp-time-direct', this.mode === 'time');
  }

  // ==================== Публичные методы для работы с событиями ====================

  /**
   * Очистить выбранное значение и связанные input элементы.
   * @returns Экземпляр DateTimePicker для цепочки вызовов
   */
  clear(): this {
    this.rangePreviewDate = null;
    this.selectedDate = [];
    this.appliedDate = [];
    this.selectedTime = this.getTimeValue(this.today);
    this.appliedTime = this.selectedTime;
    this.setShowingDate(this.resolveDefaultDate());
    this.syncTimeUi();
    this.applySelectedDateToInput();
    this.changeDate();
    this.updateSelectedDateLabel();
    return this;
  }

  /**
   * Открыть пикер программно.
   * Если пикер уже открыт, ничего не делает.
   */
  open(): void {
    if (this.isPickerOpen) return;
    this.setPickerOpen(true);
  }

  /**
   * Закрыть пикер программно.
   * Если пикер уже закрыт, ничего не делает.
   */
  close(): void {
    if (!this.isPickerOpen) return;
    this.setPickerOpen(false);
  }

  /**
   * Получить отформатированное значение пикера.
   * @returns Строковое значение из скрытого input (в формате valueFormat)
   */
  getValue(): string {
    if (this.options.workType === WORK_TYPE.MULTI && this.rangeEndInputElement) {
      const startValue = this.inputElement?.value ?? '';
      const endValue = this.rangeEndInputElement.value ?? '';
      return [startValue, endValue].filter(Boolean).join(this.options.rangeSeparator);
    }
    return this.inputElement?.value ?? '';
  }

  /**
   * Установить значение пикера программно.
   * Парсит строку в соответствии с valueFormat и обновляет календарь.
   * @param value Строковое значение в формате valueFormat
   */
  setValue(value: string): void {
    if (!this.inputElement) return;
    if (value.trim() === '') {
      this.clear();
      return;
    }
    const parts = this.options.workType === WORK_TYPE.MULTI ? this.splitRangeValue(value) : [value];
    const dates = parts.map((part, index) =>
      this.parseValueInputValue(
        part,
        this.getManagedInputType(
          index === 1 && this.rangeEndInputElement ? this.rangeEndInputElement : this.inputElement!,
        ),
      ),
    );
    if (dates.some(date => date === null)) throw new RangeError('Invalid picker value');
    this.setDate(dates as Date[]);
  }

  /**
   * Получить выбранную дату как объект Date.
   * @returns Date | Date[] | null в зависимости от режима выбора
   */
  getDate(): Date | Date[] | null {
    return this.getSelectedValuePayload();
  }

  /**
   * Установить дату программно.
   * @param date Дата (Date для single, Date[] для multi, или null для очистки)
   */
  setDate(date: Date | Date[] | null): void {
    if (date === null || (Array.isArray(date) && date.length === 0)) {
      this.clear();
      return;
    }
    const dates = Array.isArray(date) ? date : [date];
    if (
      dates.length > (this.options.workType === WORK_TYPE.MULTI ? 2 : 1) ||
      dates.some(item => !(item instanceof Date) || !this.isDateValid(item)) ||
      !this.isSelectionValid(dates)
    ) {
      throw new RangeError('Invalid or unavailable picker date/range');
    }
    this.selectedDate = dates.map(item => this.cloneDate(item));
    this.selectedTime = this.getTimeValue(dates[0]);
    this.appliedDate = this.selectedDate.map(item => this.cloneDate(item));
    this.appliedTime = this.selectedTime;
    this.applySelectedDateToInput();
    this.setShowingDate(dates[0]);
    this.changeDate();
    this.syncTimeUi();
  }

  /**
   * Подписаться на событие DateTimePicker
   * @param event Тип события
   * @param handler Обработчик события
   * @param options Опции подписки
   * @returns Экземпляр DateTimePicker для цепочки вызовов
   */
  on<T extends DateTimePickerEventType>(
    event: T,
    handler: DateTimePickerEventHandler<T>,
    options?: { once?: boolean },
  ): this {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.eventEmitter.on(`dtp:${event}`, handler as any, options);
    return this;
  }

  /**
   * Подписаться на событие один раз
   * @param event Тип события
   * @param handler Обработчик события
   * @returns Экземпляр DateTimePicker для цепочки вызовов
   */
  once<T extends DateTimePickerEventType>(event: T, handler: DateTimePickerEventHandler<T>): this {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.eventEmitter.once(`dtp:${event}`, handler as any);
    return this;
  }

  /**
   * Отписаться от события
   * @param event Тип события
   * @param handler Обработчик события (если не указан, отписываются все обработчики)
   * @returns Экземпляр DateTimePicker для цепочки вызовов
   */
  off<T extends DateTimePickerEventType>(event: T, handler?: DateTimePickerEventHandler<T>): this {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.eventEmitter.off(`dtp:${event}`, handler as any);
    return this;
  }

  /**
   * Вызвать событие (внутренний метод)
   * @param event Тип события
   * @param detail Детали события
   * @returns true если были обработчики, иначе false
   */
  protected emit<T extends DateTimePickerEventType>(
    event: T,
    detail: IDateTimePickerEventMap[T],
  ): boolean {
    // Приведение типа необходимо, потому что InternalDateTimePickerEventMap[`dtp:${T}`]
    // по определению равно DateTimePickerEventMap[T]
    return this.eventEmitter.emit(
      `dtp:${event}`,
      detail as InternalDateTimePickerEventMap[`dtp:${T}`],
    );
  }

  /**
   * Проверить, есть ли подписчики на событие
   * @param event Тип события
   * @returns true если есть подписчики, иначе false
   */
  hasListeners(event: DateTimePickerEventType): boolean {
    return this.eventEmitter.hasListeners(`dtp:${event}`);
  }

  /**
   * Получить количество подписчиков на событие
   * @param event Тип события
   * @returns Количество подписчиков
   */
  listenerCount(event: DateTimePickerEventType): number {
    return this.eventEmitter.listenerCount(`dtp:${event}`);
  }

  /**
   * Удалить все подписчики
   * @param event Тип события (если не указан, удаляются все подписчики)
   * @returns Экземпляр DateTimePicker для цепочки вызовов
   */
  removeAllListeners(event?: DateTimePickerEventType): this {
    if (event) {
      this.eventEmitter.removeAllListeners(`dtp:${event}`);
    } else {
      this.eventEmitter.removeAllListeners();
    }
    return this;
  }

  /**
   * Диспатч нативного события на input элементе
   * @param type Тип события (input, change и т.д.)
   */
  protected dispatchInputEvent(type: string): void {
    if (this.inputElement) {
      this.inputElement.dispatchEvent(new Event(type, { bubbles: true }));
    }
    if (this.rangeEndInputElement instanceof HTMLInputElement) {
      this.rangeEndInputElement.dispatchEvent(new Event(type, { bubbles: true }));
    }
  }

  /**
   * Проверяет, инициализирован ли пикер на элементе
   * @param element
   */
  static isInitialized(element: HTMLElement | string): boolean {
    return instanceManager.isInitialized(element);
  }

  /**
   * Возвращает экземпляр пикера по оригинальному ID
   * @param element
   */
  static getInstance(element: HTMLElement | string): DateTimePicker | undefined {
    return instanceManager.getInstance(element);
  }

  /**
   * Уничтожает пикер по оригинальному ID
   * @param element
   */
  static destroy(element: HTMLElement | string): boolean {
    return instanceManager.destroy(element);
  }

  /**
   * Возвращает все активные экземпляры
   */
  static getAllInstances(): Map<string, DateTimePicker> {
    return instanceManager.getAllInstances();
  }

  /**
   * Пересоздаёт пикер на элементе с новыми опциями
   * @param element - HTML элемент
   * @param options - новые опции
   * @returns новый экземпляр или существующий
   */
  static reinit(element: HTMLElement | string, options?: Partial<IOption>): DateTimePicker {
    const existingInstance = instanceManager.getInstance(element);
    const targetElement = existingInstance?.inputElement ?? instanceManager.resolveElement(element);

    if (!targetElement) {
      throw new Error('Элемент не найден');
    }

    if (existingInstance) {
      existingInstance.destroy();
    }

    return new DateTimePicker(targetElement, options);
  }

  /**
   * Уничтожает экземпляр пикера, удаляет DOM элементы и обработчики
   */
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.isPickerOpen = false;
    this.unlockBody();
    // 1. Сначала удаляем из хранилища через менеджер
    this.unregisterInstance(); // ← вызываем метод, который использует InstanceManager

    // 2. Удаляем обработчики формы и глобальные обработчики
    this.manualSubmitHandlers.forEach(({ form, handler }) => {
      form.removeEventListener('submit', handler, { capture: true });
    });
    this.manualSubmitHandlers = [];

    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    if (this.keyboardNavigationHandler) {
      document.removeEventListener('keydown', this.keyboardNavigationHandler);
      this.keyboardNavigationHandler = null;
    }

    this.cleanupHandlers.splice(0).forEach(cleanup => cleanup());
    this.inputIcons.forEach(icon => icon.remove());
    this.inputIcons.clear();
    this.displayInputElement?.remove();
    this.rangeEndDisplayInputElement?.remove();
    this.displayInputElement = null;
    this.rangeEndDisplayInputElement = null;
    this.inputSnapshots.forEach((attributes, input) => {
      const value = input.value;
      attributes.forEach((original, name) => {
        if (original === null) input.removeAttribute(name);
        else input.setAttribute(name, original);
      });
      input.value = value;
    });
    this.inputSnapshots.clear();

    // 5. Удаляем весь хост-контейнер, если он был создан
    const host = this.createdHostElement ? this.instanseElement : null;
    if (host instanceof HTMLElement) {
      // Если это обёртка, перемещаем input обратно
      if (this.inputElement && host.parentNode) {
        host.parentNode.insertBefore(this.inputElement, host);
      }
      host.remove();
    } else if (this.instanseElement !== this.inputElement) {
      // Удаляем только пикер, оставляя хост
      const wrapper = this.getPickerWrapper();
      if (wrapper) {
        wrapper.remove();
      }
    }

    // 6. Эмитим событие destroy
    this.emit('destroy', { picker: this });

    // 7. Очищаем все события
    this.removeAllListeners();
  }

  /**
   * Обновляет опции пикера
   * @param options - новые опции
   * @returns экземпляр для цепочки вызовов
   */
  updateOptions(options: Partial<IOption>): this {
    const structural: Array<keyof IOption> = [
      'mode',
      'workType',
      'rangeEndSelector',
      'timezone',
      'manualInput',
      'lang',
      'fallbackLang',
      'translations',
    ];
    for (const key of structural) {
      if (key in options && options[key] !== this.options[key]) {
        throw new Error(`Option "${key}" requires DateTimePicker.reinit()`);
      }
    }
    const next: IResolvedOption = {
      ...this.options,
      ...options,
      minDate: this.parseDateOption(options.minDate ?? this.options.minDate),
      maxDate: this.parseDateOption(options.maxDate ?? this.options.maxDate),
      openToDate: this.parseDateOption(options.openToDate ?? this.options.openToDate),
    };
    for (const key of ['minDate', 'maxDate', 'openToDate'] as const) {
      if (key in options) next[key] = this.parseDateOption(options[key] ?? null);
    }
    if (next.minDate && next.maxDate && next.minDate > next.maxDate) {
      throw new RangeError('minDate must not exceed maxDate');
    }
    const disabledKeys = this.makeDisabledDayKeys(next.disabledDates);
    this.options = next;
    this.disabledDayKeys = disabledKeys;
    if (options.startWeek) {
      this.weekDays = new WeekDays(options.startWeek);
      this.setCalendarHeader();
    }
    this.syncDisplayInputPlaceholders();
    if (!this.isSelectionValid(this.selectedDate)) this.clear();
    else this.syncSelectedDateToInputs();
    this.changeDate();
    this.syncTimeUi();
    return this;
  }
}
